import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import "./css/AdminPage.css";
import { buildApiUrl, SITE_BASE_URL } from "../config/runtime";
import { Line } from "react-chartjs-2";
import AdminSiteEditor from "./admin/AdminSiteEditor";
import { useAdminSiteContent } from "./admin/useAdminSiteContent";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const adminApi = axios.create({
  baseURL: buildApiUrl("/chat/admin")
});

const EMPTY_FUNNEL_PERIOD = {
  start: null,
  end: null,
  newUsers: 0,
  newUsersPrev: 0,
  newUsersDeltaPct: 0,
  joined: 0,
  joinRate: 0,
  activation: {
    activated: 0,
    activated7: 0,
    rate: 0,
    rate7: 0,
    fromJoinedRate: 0,
    timeAvgMin: 0,
    timeMedianMin: 0,
    p50: 0,
    p75: 0,
    p90: 0
  },
  aha: {
    users: 0,
    rateFromActivated: 0,
    avgRequestsDay1: 0,
    noAhaRate: 0
  },
  paywall: {
    viewUsers: 0,
    rateFromAha: 0,
    avgViewsBeforePurchase: 0,
    medianViewsBeforePurchase: 0,
    dist: { one: 0, twoThree: 0, fourPlus: 0 }
  },
  checkout: {
    openUsers: 0,
    rateFromPaywall: 0
  },
  purchase: {
    users: 0,
    usersWithAha: 0,
    usersWithoutAha: 0,
    events: 0,
    crPaywallPurchase: 0,
    crCheckoutPurchase: 0,
    crAhaPurchase: 0,
    ahaLag: { samples: 0, avgDays: 0, medianDays: 0, day0: 0, day1: 0, day3: 0, day7: 0 }
  }
};

const EMPTY_PRODUCT_BREAKDOWN = {
  lite: { count: 0, revenue: 0 },
  pro: { count: 0, revenue: 0 },
  max: { count: 0, revenue: 0 },
  tokens: { count: 0, revenue: 0 },
  roulette: { count: 0, revenue: 0 },
  photosession: { count: 0, revenue: 0 },
  ocr: { count: 0, revenue: 0 },
  other: { count: 0, revenue: 0 }
};

const EMPTY_MONEY_PERIOD = {
  revenue: 0,
  purchases: 0,
  purchaseUsers: 0,
  arppu: 0,
  tokens: 0,
  cost: 0,
  margin: 0,
  bySource: { vkdonut: 0, yookassa: 0, other: 0 },
  byPlan: {
    lite: { count: 0, revenue: 0 },
    pro: { count: 0, revenue: 0 },
    max: { count: 0, revenue: 0 }
  },
  byProduct: { ...EMPTY_PRODUCT_BREAKDOWN },
  renewals: 0,
  subscriptions: {
    new: { users: 0, events: 0, revenue: 0 },
    renewals: { users: 0, events: 0, revenue: 0 },
    total: { users: 0, events: 0, revenue: 0 }
  },
  oneTime: { users: 0, events: 0, revenue: 0 },
  paymentFails: 0,
  subscriptionCancels: 0
};

const EMPTY_ANALYTICS = {
  generatedAt: null,
  updatedAt: null,
  refreshing: false,
  stale: false,
  error: null,
  periods: {
    day: { ...EMPTY_FUNNEL_PERIOD },
    week: { ...EMPTY_FUNNEL_PERIOD },
    month: { ...EMPTY_FUNNEL_PERIOD }
  },
  dropOff: { steps: [], bottleneck: null },
  money: {
    day: { ...EMPTY_MONEY_PERIOD },
    week: { ...EMPTY_MONEY_PERIOD },
    month: { ...EMPTY_MONEY_PERIOD },
    activePaid: 0,
    activeByPlan: { lite: 0, pro: 0, max: 0 },
    newPayersMonth: 0,
    avgTimeToFirstPurchaseDays: 0,
    repeatPurchaseCount: 0,
    repeatPurchaseRate: 0,
    renewRate: 0,
    revenueFromOldUsersShare: 0,
    lifecycleDays: 0,
    avgMonths: 0,
    ltv: 0,
    cac: 0,
    ltvToCac: 0,
    avgWeeklyNewPayers: 0,
    goalWeeks: 0
  },
  retention: { d1: 0, d7: 0, d30: 0 },
  cohorts: { rows: [], insights: [] },
  roles: {
    newUsers: { total: 0, roles: { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0 }, shares: { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0 } },
    payers: { total: 0, roles: { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0 }, shares: { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0 } }
  },
  weekly: {
    purchaseTypes: {
      subscriptionNew: { users: 0, events: 0, revenue: 0, rate: 0 },
      oneTime: { users: 0, events: 0, revenue: 0, rate: 0 }
    },
    entryPoints: { rows: [], bestConversion: null, bestAvgCheck: null },
    roles: {
      school: { users: 0, payers: 0, payRate: 0, subsRevenue: 0, oneTimeRevenue: 0 },
      other: { users: 0, payers: 0, payRate: 0, subsRevenue: 0, oneTimeRevenue: 0 }
    },
    oneTimeToSubscription: { oneTimeUsers: 0, converted7: 0, converted30: 0, rate7: 0, rate30: 0 },
    subscriptions: {
      new: { users: 0, events: 0, revenue: 0 },
      renewals: { users: 0, events: 0, revenue: 0 },
      cancellations: 0,
      net: 0
    }
  },
  executiveSummary: [],
  conclusions: { bottleneck: null, growthMetric: null, focus: null, goalWeeks: 0 },
  dashboard: { date: "", text: "" }
};

const ROLE_LABELS = {
  1: "🎓 Воспитатель ДОУ",
  2: "📚 Учитель СОШ",
  3: "👩‍💼 Методист/завуч",
  4: "🤷 Другое",
  0: "Не выбрали"
};

const PRODUCT_GROUP_LABELS = [
  { key: "lite", label: "Lite" },
  { key: "pro", label: "Pro" },
  { key: "max", label: "Max" },
  { key: "tokens", label: "Токены (пакеты)" },
  { key: "roulette", label: "Рулетка" },
  { key: "photosession", label: "Фотосессия" },
  { key: "ocr", label: "Решение по фото" },
  { key: "other", label: "Другое" }
];

const ENTRY_POINT_LABELS = {
  menu: "menu (меню)",
  after_aha: "after_aha (после Aha)",
  tokens_low: "tokens_low (мало токенов)",
  tokens_zero: "tokens_zero (0 токенов)",
  after_photosession_result: "after_photosession_result (фотосессия)",
  after_photo_task_result: "after_photo_task_result (OCR)",
  after_limit_hit: "after_limit_hit (лимиты/очередь)",
  push: "push",
  wheel: "wheel (рулетка)",
  unknown: "unknown"
};

const ROULETTE_ACCENT_PRESETS = [
  "#7CE7FF",
  "#76F2C2",
  "#6CDBFF",
  "#8EF6A8",
  "#FFD2F5",
  "#FFB86B",
  "#FF6B6B",
  "#FF7BC7",
  "#A084FF",
  "#FFD166"
];

const ROULETTE_ICON_PRESETS = [
  "/roulette/tokens.svg",
  "/roulette/discount.svg",
  "/roulette/jackpot.svg",
  "/roulette/lite.svg",
  "/roulette/pro.svg",
  "/roulette/premium.svg",
  "/roulette/ruble.svg",
  "/roulette/sticker.svg"
];

const TEMPLATE_EMOJI_PRESETS = [
  "⚡",
  "🎓",
  "✍️",
  "💼",
  "🎨",
  "🧠",
  "📈",
  "🛠️",
  "📚",
  "🎯",
  "🔥",
  "✅",
  "🤖",
  "📝",
  "📌",
  "💡"
];

const SYSTEM_PROMPT_FIELDS = [
  {
    key: "default",
    title: "Системный prompt по умолчанию",
    description: "Используется как базовый system prompt для новых пользователей и при сбросе сессии."
  },
  {
    key: "reportAttestationTask",
    title: "Промт задачи: Для отчёта / аттестации",
    description: "Основная инструкция, которая добавляется к исходному материалу перед переработкой в отчётный формат."
  },
  {
    key: "reportAttestationSystem",
    title: "System prompt: Для отчёта / аттестации",
    description: "Системная роль модели для сценария переработки материала в отчётный формат."
  }
];

const MetricLabel = ({ title, hint }) => (
  <div className="metric-label">
    <div className="metric-title">{title}</div>
    {hint ? <div className="metric-hint">{hint}</div> : null}
  </div>
);

const MetricKpi = ({ title, value, hint }) => (
  <div className="metric-kpi">
    <div className="metric-title">{title}</div>
    <div className="metric-value">{value}</div>
    {hint ? <div className="metric-hint">{hint}</div> : null}
  </div>
);

export default function AdminPage() {
  const [code, setCode] = useState("");
  const [verified, setVerified] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchId, setSearchId] = useState("");
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState({
    registrations: Array(7).fill(0),
    dailyActive: 0,
    totalUsers: 0,
    premiumUsers: 0,
    lite: { count: 0, sum: 0 },
    pro: { count: 0, sum: 0 },
    premium: { count: 0, sum: 0 },
    planPrices: { lite: 0, pro: 0, max: 0 }
  });

  const [actionData, setActionData] = useState({
    balance: 0,
    premiumId: 1,
    premiumDays: 1
  });

  const [adminToken, setAdminToken] = useState("");
  // Добавляем новые состояния
  const [logId, setLogId] = useState("");
  const [log, setLog] = useState(null);
  const [userLogs, setUserLogs] = useState([]);
  const [chartData, setChartData] = useState({ labels: [], data: [] });
  const [lastLogs, setLastLogs] = useState([]);
  const [analyticsData, setAnalyticsData] = useState({ ...EMPTY_ANALYTICS });
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [mailingData, setMailingData] = useState({
    text: "",
    target: "all",
    attachments: ""
  });
  const [mailingImages, setMailingImages] = useState([]);
  const [mailingButtons, setMailingButtons] = useState([]);
  const [mailingSending, setMailingSending] = useState(false);
  const [mailingResult, setMailingResult] = useState(null);
  const [mailingFileKey, setMailingFileKey] = useState(0);
  const [mailingProgress, setMailingProgress] = useState(null);
  const [mailingStartFromUserId, setMailingStartFromUserId] = useState("");
  const [mailingRate, setMailingRate] = useState(50);
  const [mailingPreview, setMailingPreview] = useState(null);
  const mailingProgressTimer = useRef(null);
  const mailingPreviewTimer = useRef(null);
  const analyticsRefreshTimer = useRef(null);
  const [localesList, setLocalesList] = useState([]);
  const [localeLang, setLocaleLang] = useState("ru");
  const [localeData, setLocaleData] = useState({});
  const [localeFilter, setLocaleFilter] = useState("");
  const [localeSaving, setLocaleSaving] = useState(false);
  const [localeDirty, setLocaleDirty] = useState(false);
  const [rouletteConfig, setRouletteConfig] = useState(null);
  const [rouletteLoading, setRouletteLoading] = useState(false);
  const [rouletteSaving, setRouletteSaving] = useState(false);
  const [rouletteDirty, setRouletteDirty] = useState(false);
  const [rouletteTab, setRouletteTab] = useState("free");
  const [photosessionConfig, setPhotosessionConfig] = useState(null);
  const [photosessionLoading, setPhotosessionLoading] = useState(false);
  const [photosessionSaving, setPhotosessionSaving] = useState(false);
  const [photosessionDirty, setPhotosessionDirty] = useState(false);
  const [photosessionUploadingIndex, setPhotosessionUploadingIndex] = useState(null);
  const [templatesConfig, setTemplatesConfig] = useState(null);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesSaving, setTemplatesSaving] = useState(false);
  const [templatesDirty, setTemplatesDirty] = useState(false);
  const [sectionsOpen, setSectionsOpen] = useState({
    overview: true,
    analytics: false,
    locales: false,
    roulette: false,
    photosession: false,
    templates: false,
    stats: false,
    user: false,
    mailing: false,
    logById: false,
    logsUser: false,
    logsChart: false,
    lastLogs: false
  });
  const {
    siteConfig,
    siteLoading,
    siteSaving,
    siteDirty,
    siteEditorTab,
    setSiteEditorTab,
    saveSiteConfig,
    updateSiteConfig,
    resetSiteConfig
  } = useAdminSiteContent({ adminApi, verified, adminToken });

  const fetchLastLogs = useCallback(async (token = adminToken) => {
    if (!token) return;
    try {
      const res = await adminApi.post("/last-logs", { adminToken: token });
      setLastLogs(res.data.logs || []);
    } catch (err) {
      console.error("Ошибка получения последних логов:", err);
    }
  }, [adminToken]);
  
  // После успешной проверки кода
  useEffect(() => {
    if (verified && adminToken) {
      fetchLastLogs(adminToken);
    }
  }, [verified, adminToken, fetchLastLogs]);
  // Функция для получения графика сообщений за 7 дней
  const fetchLogsChart = async (token) => {
    try {
      const res = await adminApi.post("/log-stats", { adminToken: token });
      setChartData({ labels: res.data.labels, data: res.data.data });
    } catch (err) {
      console.error(err);
    }
  };

  // Поиск лога по ID
  const handleSearchLogById = async () => {
    if (!logId) return;
    try {
      const res = await adminApi.post("/log-by-id", { logId, adminToken });
      setLog(res.data.log || null);
      if (!res.data.log) alert("Лог не найден");
    } catch (err) {
      console.error(err);
      setLog(null);
      alert("Ошибка при поиске лога");
    }
  };

  // Получение логов пользователя по user_id
  const handleSearchLogsByUser = async () => {
    if (!searchId) return;
    try {
      const res = await adminApi.post("/logs-by-user", { userId: Number(searchId), adminToken });
      setUserLogs(res.data.logs || []);
    } catch (err) {
      console.error(err);
      setUserLogs([]);
      alert("Ошибка при получении логов пользователя");
    }
  };

  // В useEffect после успешной верификации можно сразу загрузить график
  useEffect(() => {
    if (verified) fetchLogsChart(adminToken);
  }, [verified, adminToken]);
  useEffect(() => {
    if (verified && adminToken) fetchAnalytics(adminToken);
  }, [verified, adminToken]);
  useEffect(() => {
    if (verified && adminToken) fetchRouletteConfig(adminToken);
  }, [verified, adminToken]);
  useEffect(() => {
    if (verified && adminToken) fetchPhotosessionConfig(adminToken);
  }, [verified, adminToken]);
  useEffect(() => {
    if (verified && adminToken) fetchTemplatesConfig(adminToken);
  }, [verified, adminToken]);

  useEffect(() => {
    if (verified && adminToken && localeLang) {
      fetchLocaleData(adminToken, localeLang);
    }
  }, [verified, adminToken, localeLang]);
  useEffect(() => {
    // Генерация кода при загрузке
    axios
      .get(buildApiUrl("/chat/admin/generate-code"))
      .then(() => console.log("Код отправлен в VK"))
      .catch((err) => console.error(err));
  }, []);

  const handleVerify = async () => {
    if (!code) return;
    setLoading(true);
    try {
      const res = await adminApi.post("/verify-code", { code });
      if (res.data.success) {
        setVerified(true);
        setAdminToken(res.data.adminToken);
        fetchStats(res.data.adminToken);
        fetchAnalytics(res.data.adminToken);
        fetchLastLogs(res.data.adminToken);
        fetchLocalesList(res.data.adminToken);
        fetchRouletteConfig(res.data.adminToken);
        fetchPhotosessionConfig(res.data.adminToken);
        fetchTemplatesConfig(res.data.adminToken);
      } else {
        alert(res.data.error || "Неверный код!");
      }
    } catch (err) {
      console.error(err);
      alert("Ошибка сервера");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async (token) => {
    try {
      const res = await adminApi.post("/stats", { adminToken: token });
      setStats(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchAnalytics = async (token, force = false) => {
    if (!token) return;
    setAnalyticsLoading(true);
    try {
      const res = await adminApi.post("/analytics", { adminToken: token, force });
      const payload = res.data || {};
      const periods = payload.periods || {};
      const money = payload.money || {};
      const roles = payload.roles || {};
      const cohorts = payload.cohorts || {};
      const retention = payload.retention || {};
      const dropOff = payload.dropOff || {};
      const conclusions = payload.conclusions || {};
      const dashboard = payload.dashboard || {};
      const weekly = payload.weekly || {};
      setAnalyticsData({
        ...EMPTY_ANALYTICS,
        ...payload,
        periods: {
          day: { ...EMPTY_FUNNEL_PERIOD, ...(periods.day || {}) },
          week: { ...EMPTY_FUNNEL_PERIOD, ...(periods.week || {}) },
          month: { ...EMPTY_FUNNEL_PERIOD, ...(periods.month || {}) }
        },
        money: {
          ...EMPTY_ANALYTICS.money,
          ...money,
          day: { ...EMPTY_MONEY_PERIOD, ...(money.day || {}) },
          week: { ...EMPTY_MONEY_PERIOD, ...(money.week || {}) },
          month: { ...EMPTY_MONEY_PERIOD, ...(money.month || {}) }
        },
        roles: {
          ...EMPTY_ANALYTICS.roles,
          ...roles
        },
        weekly: {
          ...EMPTY_ANALYTICS.weekly,
          ...weekly,
          purchaseTypes: {
            ...EMPTY_ANALYTICS.weekly.purchaseTypes,
            ...(weekly.purchaseTypes || {}),
            subscriptionNew: {
              ...EMPTY_ANALYTICS.weekly.purchaseTypes.subscriptionNew,
              ...(weekly.purchaseTypes?.subscriptionNew || {})
            },
            oneTime: {
              ...EMPTY_ANALYTICS.weekly.purchaseTypes.oneTime,
              ...(weekly.purchaseTypes?.oneTime || {})
            }
          },
          entryPoints: {
            ...EMPTY_ANALYTICS.weekly.entryPoints,
            ...(weekly.entryPoints || {})
          },
          roles: {
            ...EMPTY_ANALYTICS.weekly.roles,
            ...(weekly.roles || {})
          },
          oneTimeToSubscription: {
            ...EMPTY_ANALYTICS.weekly.oneTimeToSubscription,
            ...(weekly.oneTimeToSubscription || {})
          },
          subscriptions: {
            ...EMPTY_ANALYTICS.weekly.subscriptions,
            ...(weekly.subscriptions || {})
          }
        },
        cohorts: {
          ...EMPTY_ANALYTICS.cohorts,
          ...cohorts
        },
        retention: {
          ...EMPTY_ANALYTICS.retention,
          ...retention
        },
        dropOff: {
          ...EMPTY_ANALYTICS.dropOff,
          ...dropOff
        },
        conclusions: {
          ...EMPTY_ANALYTICS.conclusions,
          ...conclusions
        },
        dashboard: {
          ...EMPTY_ANALYTICS.dashboard,
          ...dashboard
        }
      });
    } catch (err) {
      console.error("Ошибка аналитики:", err);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  useEffect(() => {
    if (!verified || !adminToken) return;
    if (!analyticsData.refreshing) return;
    if (analyticsRefreshTimer.current) {
      clearTimeout(analyticsRefreshTimer.current);
    }
    analyticsRefreshTimer.current = setTimeout(() => {
      fetchAnalytics(adminToken);
    }, 2000);
    return () => {
      if (analyticsRefreshTimer.current) {
        clearTimeout(analyticsRefreshTimer.current);
        analyticsRefreshTimer.current = null;
      }
    };
  }, [analyticsData.refreshing, adminToken, verified]);

  const fetchLocalesList = useCallback(async (token) => {
    if (!token) return;
    try {
      const res = await adminApi.post("/locales/list", { adminToken: token });
      const list = Array.isArray(res.data?.locales) ? res.data.locales : [];
      setLocalesList(list);
      if (list.length && !list.includes(localeLang)) {
        setLocaleLang(list[0]);
      }
    } catch (err) {
      console.error("Ошибка списка локалей:", err);
    }
  }, [localeLang]);

  useEffect(() => {
    if (verified && adminToken) fetchLocalesList(adminToken);
  }, [verified, adminToken, fetchLocalesList]);

  const fetchLocaleData = async (token, lang) => {
    if (!token || !lang) return;
    try {
      const res = await adminApi.post("/locales/get", {
        adminToken: token,
        lang
      });
      setLocaleData(res.data?.data || {});
      setLocaleDirty(false);
    } catch (err) {
      console.error("Ошибка локали:", err);
    }
  };

  const fetchRouletteConfig = async (token) => {
    if (!token) return;
    setRouletteLoading(true);
    try {
      const res = await adminApi.post("/roulette/get", { adminToken: token });
      setRouletteConfig(res.data || null);
      setRouletteDirty(false);
    } catch (err) {
      console.error("Ошибка загрузки рулетки:", err);
    } finally {
      setRouletteLoading(false);
    }
  };

  const handleSaveRoulette = async () => {
    if (!adminToken || !rouletteConfig) return;
    setRouletteSaving(true);
    try {
      await adminApi.post("/roulette/save", {
        adminToken,
        config: rouletteConfig
      });
      setRouletteDirty(false);
    } catch (err) {
      console.error("Ошибка сохранения рулетки:", err);
      alert("Ошибка сохранения рулетки");
    } finally {
      setRouletteSaving(false);
    }
  };

  const fetchPhotosessionConfig = async (token) => {
    if (!token) return;
    setPhotosessionLoading(true);
    try {
      const res = await adminApi.post("/photosession/get", { adminToken: token });
      setPhotosessionConfig(res.data || null);
      setPhotosessionDirty(false);
    } catch (err) {
      console.error("Ошибка загрузки фотосессии:", err);
    } finally {
      setPhotosessionLoading(false);
    }
  };

  const handleSavePhotosession = async () => {
    if (!adminToken || !photosessionConfig) return;
    setPhotosessionSaving(true);
    try {
      const res = await adminApi.post("/photosession/save", {
        adminToken,
        config: photosessionConfig
      });
      if (res.data?.config) {
        setPhotosessionConfig(res.data.config);
      }
      setPhotosessionDirty(false);
    } catch (err) {
      console.error("Ошибка сохранения фотосессии:", err);
      alert("Ошибка сохранения фотосессии");
    } finally {
      setPhotosessionSaving(false);
    }
  };

  const fetchTemplatesConfig = async (token) => {
    if (!token) return;
    setTemplatesLoading(true);
    try {
      const res = await adminApi.post("/templates/get", { adminToken: token });
      setTemplatesConfig(res.data || null);
      setTemplatesDirty(false);
    } catch (err) {
      console.error("Ошибка загрузки шаблонов:", err);
    } finally {
      setTemplatesLoading(false);
    }
  };

  const handleSaveTemplates = async () => {
    if (!adminToken || !templatesConfig) return;
    setTemplatesSaving(true);
    try {
      const res = await adminApi.post("/templates/save", {
        adminToken,
        config: templatesConfig
      });
      if (res.data?.config) {
        setTemplatesConfig(res.data.config);
      }
      setTemplatesDirty(false);
    } catch (err) {
      console.error("Ошибка сохранения шаблонов:", err);
      alert("Ошибка сохранения шаблонов");
    } finally {
      setTemplatesSaving(false);
    }
  };

  const handlePhotosessionPreviewUpload = async (styleIndex, file) => {
    if (!adminToken || !file) return;
    setPhotosessionUploadingIndex(styleIndex);
    try {
      const formData = new FormData();
      formData.append("image", file);
      const res = await adminApi.post("/photosession/upload-preview", formData, {
        headers: { "x-admin-token": adminToken }
      });
      const attachment = String(res.data?.attachment || "").trim();
      if (!attachment) {
        throw new Error("empty attachment");
      }
      updatePhotosessionStyle(styleIndex, { previewAttachment: attachment });
    } catch (err) {
      console.error("Ошибка загрузки превью фотосессии:", err);
      alert("Ошибка загрузки картинки");
    } finally {
      setPhotosessionUploadingIndex(null);
    }
  };

  const handleSaveLocales = async () => {
    if (!adminToken || !localeLang) return;
    setLocaleSaving(true);
    try {
      await adminApi.post("/locales/update", {
        adminToken,
        lang: localeLang,
        data: localeData
      });
      setLocaleDirty(false);
    } catch (err) {
      console.error("Ошибка сохранения локали:", err);
      alert("Ошибка сохранения локали");
    } finally {
      setLocaleSaving(false);
    }
  };

  const handleSearch = async () => {
    if (!searchId) return;
    try {
      const res = await adminApi.post("/search-user", {
        id: Number(searchId),
        adminToken
      });
      setUser(res.data.user || null);
      if (!res.data.user) alert("Пользователь не найден");
    } catch (err) {
      console.error(err);
      setUser(null);
      alert("Пользователь не найден");
    }
  };

  const handleAction = async (actionType) => {
    if (!user) return;

    try {
      const payload = { id: user.id, adminToken };

      if (actionType === "balance") {
        const amount = Number(actionData.balance);
        if (amount >= 0) {
          payload.action = "giveBalance";
          payload.amount = amount;
        } else {
          payload.action = "removeBalance";
          payload.amount = Math.abs(amount);
        }
      }

      if (actionType === "premium") {
        const plan = Number(actionData.premiumId);
        if (plan === 0) {
          payload.action = "removeSubscription";
        } else {
          payload.action = "giveSubscription";
          payload.plan = plan;
          payload.days = Number(actionData.premiumDays);
        }
      }

      const res = await adminApi.post("/user-action", payload);
      setUser(res.data.user);
      alert("Действие успешно выполнено");
    } catch (err) {
      console.error(err);
      alert("Ошибка при выполнении действия");
    }
  };

  const handleMailingImagesChange = (e) => {
    const files = Array.from(e.target.files || []).slice(0, 10);
    setMailingImages(files);
  };

  const resetMailing = () => {
    setMailingData({
      text: "",
      target: "all",
      attachments: ""
    });
    setMailingImages([]);
    setMailingButtons([]);
    setMailingResult(null);
    setMailingProgress(null);
    setMailingFileKey((v) => v + 1);
  };

  const addMailingButton = (type) => {
    setMailingButtons((prev) => [
      ...prev,
      {
        id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
        type,
        text: "",
        value: ""
      }
    ]);
  };

  const updateMailingButton = (id, patch) => {
    setMailingButtons((prev) =>
      prev.map((btn) => (btn.id === id ? { ...btn, ...patch } : btn))
    );
  };

  const moveMailingButton = (id, direction) => {
    setMailingButtons((prev) => {
      const index = prev.findIndex((btn) => btn.id === id);
      if (index === -1) return prev;
      const nextIndex = direction === "up" ? index - 1 : index + 1;
      if (nextIndex < 0 || nextIndex >= prev.length) return prev;
      const copy = [...prev];
      const [item] = copy.splice(index, 1);
      copy.splice(nextIndex, 0, item);
      return copy;
    });
  };

  const removeMailingButton = (id) => {
    setMailingButtons((prev) => prev.filter((btn) => btn.id !== id));
  };

  const stopMailingProgress = useCallback(() => {
    if (mailingProgressTimer.current) {
      clearInterval(mailingProgressTimer.current);
      mailingProgressTimer.current = null;
    }
  }, []);

  useEffect(() => {
    return () => stopMailingProgress();
  }, [stopMailingProgress]);

  const fetchMailingPreview = useCallback(async (target, startFromId) => {
    if (!adminToken) return;
    try {
      const res = await axios.post(
        buildApiUrl("/chat/admin/broadcast-preview"),
        {
          target,
          startFromUserId: startFromId ? Number(startFromId) : 0
        },
        { headers: { "x-admin-token": adminToken } }
      );
      setMailingPreview(res.data || null);
    } catch (err) {
      console.error(err);
      setMailingPreview(null);
    }
  }, [adminToken]);

  useEffect(() => {
    if (!verified || !adminToken) return;
    if (mailingPreviewTimer.current) {
      clearTimeout(mailingPreviewTimer.current);
    }
    mailingPreviewTimer.current = setTimeout(() => {
      fetchMailingPreview(mailingData.target, mailingStartFromUserId);
    }, 300);
    return () => {
      if (mailingPreviewTimer.current) {
        clearTimeout(mailingPreviewTimer.current);
        mailingPreviewTimer.current = null;
      }
    };
  }, [verified, adminToken, mailingData.target, mailingStartFromUserId, fetchMailingPreview]);

  const getEstimatedTotal = () => {
    if (mailingPreview && Number.isFinite(mailingPreview.remaining)) {
      return Number(mailingPreview.remaining) || 0;
    }
    if (mailingData.target === "test") return 1;
    if (mailingData.target === "conversations") return 2000;
    if (mailingData.target === "donates") return Number(stats.premiumUsers || 0);
    if (mailingData.target === "new") {
      const regs = Array.isArray(stats.registrations) ? stats.registrations : [];
      return regs.reduce((sum, value) => sum + Number(value || 0), 0);
    }
    return Number(stats.totalUsers || 0);
  };

  const getEstimatedEtaSec = () => {
    const total = getEstimatedTotal();
    const rate = Math.max(1, Number(mailingRate || 1));
    if (!total || total <= 0) return null;
    return Math.ceil(total / rate);
  };

  const fetchMailingProgress = useCallback(async (jobId) => {
    try {
      const res = await axios.post(
        buildApiUrl("/chat/admin/broadcast-status"),
        jobId ? { jobId } : {},
        { headers: { "x-admin-token": adminToken } }
      );
      const data = res.data;
      if (!data || data.status === "idle") {
        setMailingProgress(null);
        stopMailingProgress();
        return;
      }
      setMailingProgress(data);
      if (data.status === "done" || data.status === "error") {
        stopMailingProgress();
        if (data.status === "done") {
          setMailingResult({
            success: true,
            message: `Рассылка завершена. Успешно: ${data.sent}, Ошибки: ${data.failed}, Всего: ${data.total}`
          });
        } else {
          setMailingResult({
            success: false,
            message: data.error || "Ошибка при выполнении рассылки"
          });
        }
      }
    } catch (err) {
      console.error(err);
    }
  }, [adminToken, stopMailingProgress]);

  const startMailingProgress = useCallback((jobId) => {
    stopMailingProgress();
    fetchMailingProgress(jobId);
    mailingProgressTimer.current = setInterval(() => fetchMailingProgress(jobId), 1000);
  }, [fetchMailingProgress, stopMailingProgress]);

  useEffect(() => {
    if (verified && adminToken) {
      startMailingProgress();
    }
  }, [verified, adminToken, startMailingProgress]);

  const handleSendMailing = async () => {
    if (!adminToken) return alert("Нет adminToken");

    const hasText = mailingData.text && mailingData.text.trim().length > 0;
    const attachmentsValue = String(mailingData.attachments || "").trim();
    const hasAttachments = attachmentsValue.length > 0;
    if (!hasText && mailingImages.length === 0 && !hasAttachments) {
      return alert("Введите текст рассылки или добавьте изображения");
    }

    if (mailingButtons.length > 0) {
      const invalid = mailingButtons.find(
        (btn) => !btn.text.trim() || !btn.value.trim()
      );
      if (invalid) {
        return alert("Заполните текст и значение для всех кнопок");
      }
    }

    setMailingSending(true);
    setMailingResult(null);
    setMailingProgress(null);
    try {
    const formData = new FormData();
    formData.append("text", mailingData.text);
    formData.append("target", mailingData.target);
    if (mailingStartFromUserId) {
      formData.append("startFromUserId", String(mailingStartFromUserId).trim());
    }
    formData.append("ratePerSec", String(Math.max(1, Number(mailingRate || 1))));
    if (hasAttachments) {
      formData.append("attachments", attachmentsValue);
    }
    if (mailingButtons.length > 0) {
      formData.append("buttons", JSON.stringify(mailingButtons.map((btn) => ({
        type: btn.type,
        text: btn.text,
        value: btn.value
      }))));
      }
      mailingImages.forEach((file) => formData.append("images", file));

      const res = await axios.post(buildApiUrl("/chat/admin/broadcast"), formData, {
        headers: { "x-admin-token": adminToken }
      });

      const jobId = res.data?.jobId;
      const total = res.data?.total ?? 0;
      const status = res.data?.status || "running";
      setMailingProgress({
        id: jobId,
        total,
        sent: 0,
        failed: 0,
        processed: 0,
        percent: total > 0 ? 0 : 100,
        status
      });

      if (status === "done") {
        setMailingResult({
          success: true,
          message: `Рассылка завершена. Успешно: 0, Ошибки: 0, Всего: ${total}`
        });
      } else {
        startMailingProgress(jobId);
      }
    } catch (err) {
      console.error(err);
      const message = err.response?.data?.error || "Ошибка при отправке рассылки";
      setMailingResult({ success: false, message });
    } finally {
      setMailingSending(false);
    }
  };

  if (!verified) {
    return (
      <div className="admin-login">
        <div className="login-box">
          <h2>Введите код из VK</h2>
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Код из VK"
          />
          <button onClick={handleVerify} disabled={loading}>
            {loading ? "Проверка..." : "Войти"}
          </button>
        </div>
      </div>
    );
  }

  // ====== Метка для графика: последние 7 дней ======
  const oneDay = 24 * 60 * 60 * 1000;
  const now = new Date();
  const labels = Array.from({ length: 7 }, (_, i) => {
    const day = new Date(now - (6 - i) * oneDay);
    return day.toLocaleDateString("ru-RU");
  });
  const formatDateKey = (dateObj) => {
    const y = dateObj.getFullYear();
    const m = String(dateObj.getMonth() + 1).padStart(2, "0");
    const d = String(dateObj.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };
  const dayDates = Array.from({ length: 7 }, (_, i) => new Date(now - (6 - i) * oneDay));
  const dayKeys = dayDates.map(formatDateKey);
  const logsMap = (Array.isArray(chartData.labels) ? chartData.labels : []).reduce((acc, rawDate, idx) => {
    const parsed = rawDate instanceof Date ? rawDate : new Date(rawDate);
    if (Number.isNaN(parsed.getTime())) return acc;
    const key = formatDateKey(parsed);
    acc[key] = (Array.isArray(chartData.data) ? chartData.data[idx] : 0) || 0;
    return acc;
  }, {});
  const logsLabels = labels;
  const logsData = dayKeys.map((key) => logsMap[key] || 0);

  const isMailingRunning = mailingProgress && mailingProgress.status === "running";
  const mailingDisabled = mailingSending || isMailingRunning;
  const formatEta = (seconds) => {
    if (seconds === null || seconds === undefined) return "—";
    const total = Math.max(0, Number(seconds));
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    if (h > 0) return `${h}ч ${m}м`;
    if (m > 0) return `${m}м ${s}с`;
    return `${s}с`;
  };

  const periodDay = analyticsData?.periods?.day || EMPTY_FUNNEL_PERIOD;
  const periodWeek = analyticsData?.periods?.week || EMPTY_FUNNEL_PERIOD;
  const periodMonth = analyticsData?.periods?.month || EMPTY_FUNNEL_PERIOD;
  const moneyDay = analyticsData?.money?.day || EMPTY_MONEY_PERIOD;
  const moneyWeek = analyticsData?.money?.week || EMPTY_MONEY_PERIOD;
  const moneyMonth = analyticsData?.money?.month || EMPTY_MONEY_PERIOD;
  const activeByPlan = analyticsData?.money?.activeByPlan || EMPTY_ANALYTICS.money.activeByPlan;
  const retention = analyticsData?.retention || EMPTY_ANALYTICS.retention;
  const dropOff = analyticsData?.dropOff || EMPTY_ANALYTICS.dropOff;
  const cohorts = analyticsData?.cohorts || EMPTY_ANALYTICS.cohorts;
  const roles = analyticsData?.roles || EMPTY_ANALYTICS.roles;
  const weekly = analyticsData?.weekly || EMPTY_ANALYTICS.weekly;
  const conclusions = analyticsData?.conclusions || EMPTY_ANALYTICS.conclusions;
  const dashboard = analyticsData?.dashboard || EMPTY_ANALYTICS.dashboard;

  const formatNumber = (value) => {
    const num = Number(value || 0);
    return Number.isFinite(num) ? num.toLocaleString("ru-RU") : "0";
  };
  const formatMoney = (value) => `${formatNumber(value)} ₽`;
  const formatPercent = (value) => {
    const num = Number(value || 0);
    if (!Number.isFinite(num)) return "0%";
    if (num !== 0 && Math.abs(num) < 0.1) return `${num < 0 ? "-" : ""}<0.1%`;
    const rounded = Math.round(num * 10) / 10;
    return `${rounded}%`;
  };
  const formatSignedPercent = (value) => {
    const num = Number(value || 0);
    if (!Number.isFinite(num)) return "0%";
    const sign = num >= 0 ? "+" : "";
    if (num !== 0 && Math.abs(num) < 0.1) return `${sign}<0.1%`;
    const rounded = Math.round(num * 10) / 10;
    return `${sign}${rounded}%`;
  };
  const formatDateTime = (value) => {
    if (!value) return "—";
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return "—";
    return date.toLocaleString("ru-RU");
  };
  const formatShare = (value, total) => {
    const denom = Number(total || 0);
    if (!denom) return "0%";
    return formatPercent((Number(value || 0) / denom) * 100);
  };
  const formatAhaLag = (purchase) => {
    const samples = Number(purchase?.ahaLag?.samples || 0);
    if (samples <= 0) return "— / —";
    return `${formatNumber(purchase?.ahaLag?.avgDays)} / ${formatNumber(purchase?.ahaLag?.medianDays)}`;
  };
  const formatAhaLagBuckets = (purchase) => {
    const samples = Number(purchase?.ahaLag?.samples || 0);
    if (samples <= 0) return "— / — / — / —";
    return `${formatPercent(purchase?.ahaLag?.day0)} / ${formatPercent(purchase?.ahaLag?.day1)} / ${formatPercent(purchase?.ahaLag?.day3)} / ${formatPercent(purchase?.ahaLag?.day7)}`;
  };

  const formatProductCell = (period, key) => {
    const row = period?.byProduct?.[key] || EMPTY_PRODUCT_BREAKDOWN[key] || { count: 0, revenue: 0 };
    return `${formatNumber(row.count)} (${formatMoney(row.revenue)})`;
  };
  const formatEntryPoint = (value) => {
    const key = value ? String(value) : "unknown";
    return ENTRY_POINT_LABELS[key] || key;
  };

  const premiumTotal =
    (Number(stats.lite?.count || 0)) +
    (Number(stats.pro?.count || 0)) +
    (Number(stats.premium?.count || 0));
  const revenueTotal =
    (Number(stats.lite?.sum || 0)) +
    (Number(stats.pro?.sum || 0)) +
    (Number(stats.premium?.sum || 0));
  const planPrices = stats?.planPrices || {};
  const planPricesText =
    Number(planPrices.lite || 0) || Number(planPrices.pro || 0) || Number(planPrices.max || 0)
      ? `${formatMoney(planPrices.lite)} / ${formatMoney(planPrices.pro)} / ${formatMoney(planPrices.max)}`
      : "—";
  const analyticsUpdatedAt = analyticsData?.updatedAt || analyticsData?.generatedAt;
  const hasUnsavedChanges = siteDirty || localeDirty || rouletteDirty || photosessionDirty || templatesDirty;

  const localeEntries = Object.entries(localeData || {});
  const filteredLocales = localeEntries.filter(([key, value]) => {
    if (!localeFilter) return true;
    const needle = localeFilter.toLowerCase();
    return String(key).toLowerCase().includes(needle) || String(value).toLowerCase().includes(needle);
  });

  const toggleSection = (key) => {
    setSectionsOpen((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const getRoulettePrizes = () => {
    if (!rouletteConfig || !rouletteConfig[rouletteTab]) return [];
    return Array.isArray(rouletteConfig[rouletteTab].prizes)
      ? rouletteConfig[rouletteTab].prizes
      : [];
  };

  const updateRoulettePrize = (index, patch) => {
    setRouletteConfig((prev) => {
      if (!prev) return prev;
      const next = { ...prev };
      const block = { ...next[rouletteTab] };
      const prizes = [...(block.prizes || [])];
      prizes[index] = { ...prizes[index], ...patch };
      block.prizes = prizes;
      next[rouletteTab] = block;
      return next;
    });
    setRouletteDirty(true);
  };

  const removeRoulettePrize = (index) => {
    setRouletteConfig((prev) => {
      if (!prev) return prev;
      const next = { ...prev };
      const block = { ...next[rouletteTab] };
      const prizes = [...(block.prizes || [])];
      prizes.splice(index, 1);
      block.prizes = prizes;
      next[rouletteTab] = block;
      return next;
    });
    setRouletteDirty(true);
  };

  const addRoulettePrize = () => {
    const type = "tokens";
    const newPrize = {
      id: `new-${Date.now()}`,
      label: "Новый приз",
      rarity: "common",
      accent: "#7CE7FF",
      icon: "/roulette/tokens.svg",
      weight: 1,
      type,
      value: 100,
      message: ""
    };
    setRouletteConfig((prev) => {
      if (!prev) return prev;
      const next = { ...prev };
      const block = { ...next[rouletteTab] };
      const prizes = [...(block.prizes || [])];
      prizes.unshift(newPrize);
      block.prizes = prizes;
      next[rouletteTab] = block;
      return next;
    });
    setRouletteDirty(true);
  };

  const getPhotosessionStyles = () => {
    if (!photosessionConfig) return [];
    return Array.isArray(photosessionConfig.styles) ? photosessionConfig.styles : [];
  };

  const updatePhotosessionConfig = (patch) => {
    setPhotosessionConfig((prev) => {
      if (!prev) return prev;
      return { ...prev, ...patch };
    });
    setPhotosessionDirty(true);
  };

  const updatePhotosessionStyle = (index, patch) => {
    setPhotosessionConfig((prev) => {
      if (!prev) return prev;
      const styles = Array.isArray(prev.styles) ? [...prev.styles] : [];
      styles[index] = { ...(styles[index] || {}), ...patch };
      return { ...prev, styles };
    });
    setPhotosessionDirty(true);
  };

  const movePhotosessionStyle = (index, direction) => {
    setPhotosessionConfig((prev) => {
      if (!prev) return prev;
      const styles = Array.isArray(prev.styles) ? [...prev.styles] : [];
      const nextIndex = direction === "up" ? index - 1 : index + 1;
      if (nextIndex < 0 || nextIndex >= styles.length) return prev;
      const [item] = styles.splice(index, 1);
      styles.splice(nextIndex, 0, item);
      return { ...prev, styles };
    });
    setPhotosessionDirty(true);
  };

  const removePhotosessionStyle = (index) => {
    setPhotosessionConfig((prev) => {
      if (!prev) return prev;
      const styles = Array.isArray(prev.styles) ? [...prev.styles] : [];
      styles.splice(index, 1);
      return { ...prev, styles };
    });
    setPhotosessionDirty(true);
  };

  const addPhotosessionStyle = () => {
    const newStyle = {
      id: `style_${Date.now()}`,
      title: "Новый стиль",
      prompt: "",
      previewAttachment: "",
      active: true
    };
    setPhotosessionConfig((prev) => {
      const base = prev || { pageSize: 5, tokenCost: 8000, oneTimeProductId: 9, styles: [] };
      const styles = Array.isArray(base.styles) ? [newStyle, ...base.styles] : [newStyle];
      return { ...base, styles };
    });
    setPhotosessionDirty(true);
  };

  const getTemplatePrompts = () => {
    if (!templatesConfig) return [];
    return Array.isArray(templatesConfig.prompts) ? templatesConfig.prompts : [];
  };

  const getNeuroSections = () => {
    if (!templatesConfig) return [];
    return Array.isArray(templatesConfig.neuroSections) ? templatesConfig.neuroSections : [];
  };

  const getTextResultUi = () => {
    const ui = templatesConfig?.textResultUi;
    if (ui && typeof ui === "object") {
      return ui;
    }
    return {
      primaryText: "",
      primaryButtons: [],
      secondaryText: "",
      secondaryButtons: []
    };
  };

  const getSystemPrompts = () => {
    const data = templatesConfig?.systemPrompts;
    if (data && typeof data === "object" && !Array.isArray(data)) {
      return data;
    }
    return {};
  };

  const getTextResultButtons = (group) => {
    const ui = getTextResultUi();
    return Array.isArray(ui?.[group]) ? ui[group] : [];
  };

  const updateTemplatesConfig = (patch) => {
    setTemplatesConfig((prev) => {
      if (!prev) return prev;
      return { ...prev, ...patch };
    });
    setTemplatesDirty(true);
  };

  const updateTextResultUi = (patch) => {
    setTemplatesConfig((prev) => {
      if (!prev) return prev;
      const textResultUi = {
        ...(prev.textResultUi || {
          primaryText: "",
          primaryButtons: [],
          secondaryText: "",
          secondaryButtons: []
        }),
        ...patch
      };
      return { ...prev, textResultUi };
    });
    setTemplatesDirty(true);
  };

  const updateSystemPromptValue = (key, value) => {
    setTemplatesConfig((prev) => {
      const base = prev || {
        pageSize: 5,
        prompts: [],
        neuroPageSize: 5,
        neuroSections: [],
        textResultUi: {
          primaryText: "",
          primaryButtons: [],
          secondaryText: "",
          secondaryButtons: []
        },
        systemPrompts: {}
      };
      const systemPrompts = {
        ...(base.systemPrompts || {}),
        [key]: value
      };
      return { ...base, systemPrompts };
    });
    setTemplatesDirty(true);
  };

  const updateTemplatePrompt = (index, patch) => {
    setTemplatesConfig((prev) => {
      if (!prev) return prev;
      const prompts = Array.isArray(prev.prompts) ? [...prev.prompts] : [];
      prompts[index] = { ...(prompts[index] || {}), ...patch };
      return { ...prev, prompts };
    });
    setTemplatesDirty(true);
  };

  const updateNeuroSection = (index, patch) => {
    setTemplatesConfig((prev) => {
      if (!prev) return prev;
      const neuroSections = Array.isArray(prev.neuroSections) ? [...prev.neuroSections] : [];
      neuroSections[index] = { ...(neuroSections[index] || {}), ...patch };
      return { ...prev, neuroSections };
    });
    setTemplatesDirty(true);
  };

  const updateTextResultButton = (group, index, patch) => {
    setTemplatesConfig((prev) => {
      if (!prev) return prev;
      const textResultUi = {
        ...(prev.textResultUi || {
          primaryText: "",
          primaryButtons: [],
          secondaryText: "",
          secondaryButtons: []
        })
      };
      const buttons = Array.isArray(textResultUi[group]) ? [...textResultUi[group]] : [];
      buttons[index] = { ...(buttons[index] || {}), ...patch };
      textResultUi[group] = buttons;
      return { ...prev, textResultUi };
    });
    setTemplatesDirty(true);
  };

  const moveTemplatePrompt = (index, direction) => {
    setTemplatesConfig((prev) => {
      if (!prev) return prev;
      const prompts = Array.isArray(prev.prompts) ? [...prev.prompts] : [];
      const nextIndex = direction === "up" ? index - 1 : index + 1;
      if (nextIndex < 0 || nextIndex >= prompts.length) return prev;
      const [item] = prompts.splice(index, 1);
      prompts.splice(nextIndex, 0, item);
      return { ...prev, prompts };
    });
    setTemplatesDirty(true);
  };

  const moveNeuroSection = (index, direction) => {
    setTemplatesConfig((prev) => {
      if (!prev) return prev;
      const neuroSections = Array.isArray(prev.neuroSections) ? [...prev.neuroSections] : [];
      const nextIndex = direction === "up" ? index - 1 : index + 1;
      if (nextIndex < 0 || nextIndex >= neuroSections.length) return prev;
      const [item] = neuroSections.splice(index, 1);
      neuroSections.splice(nextIndex, 0, item);
      return { ...prev, neuroSections };
    });
    setTemplatesDirty(true);
  };

  const moveTextResultButton = (group, index, direction) => {
    setTemplatesConfig((prev) => {
      if (!prev) return prev;
      const textResultUi = {
        ...(prev.textResultUi || {
          primaryText: "",
          primaryButtons: [],
          secondaryText: "",
          secondaryButtons: []
        })
      };
      const buttons = Array.isArray(textResultUi[group]) ? [...textResultUi[group]] : [];
      const nextIndex = direction === "up" ? index - 1 : index + 1;
      if (nextIndex < 0 || nextIndex >= buttons.length) return prev;
      const [item] = buttons.splice(index, 1);
      buttons.splice(nextIndex, 0, item);
      textResultUi[group] = buttons;
      return { ...prev, textResultUi };
    });
    setTemplatesDirty(true);
  };

  const removeTemplatePrompt = (index) => {
    setTemplatesConfig((prev) => {
      if (!prev) return prev;
      const prompts = Array.isArray(prev.prompts) ? [...prev.prompts] : [];
      prompts.splice(index, 1);
      return { ...prev, prompts };
    });
    setTemplatesDirty(true);
  };

  const removeNeuroSection = (index) => {
    setTemplatesConfig((prev) => {
      if (!prev) return prev;
      const neuroSections = Array.isArray(prev.neuroSections) ? [...prev.neuroSections] : [];
      neuroSections.splice(index, 1);
      return { ...prev, neuroSections };
    });
    setTemplatesDirty(true);
  };

  const removeTextResultButton = (group, index) => {
    setTemplatesConfig((prev) => {
      if (!prev) return prev;
      const textResultUi = {
        ...(prev.textResultUi || {
          primaryText: "",
          primaryButtons: [],
          secondaryText: "",
          secondaryButtons: []
        })
      };
      const buttons = Array.isArray(textResultUi[group]) ? [...textResultUi[group]] : [];
      buttons.splice(index, 1);
      textResultUi[group] = buttons;
      return { ...prev, textResultUi };
    });
    setTemplatesDirty(true);
  };

  const addTemplatePrompt = () => {
    const newPrompt = {
      id: `template_${Date.now()}`,
      emoji: "⚡",
      title: "Новый шаблон",
      prompt: "",
      active: true
    };
    setTemplatesConfig((prev) => {
      const base = prev || { pageSize: 5, prompts: [] };
      const prompts = Array.isArray(base.prompts) ? [newPrompt, ...base.prompts] : [newPrompt];
      return { ...base, prompts };
    });
    setTemplatesDirty(true);
  };

  const addNeuroSection = () => {
    const newSection = {
      id: `neuro_${Date.now()}`,
      title: "Новый раздел",
      active: true,
      cost: 0,
      showCostOnButton: false,
      type: "text",
      sessionMode: "single",
      entryText: "",
      entryAttachment: "",
      prompt: "",
      resultText: "{{result}}",
      resultAttachment: ""
    };
    setTemplatesConfig((prev) => {
      const base = prev || { pageSize: 5, prompts: [], neuroPageSize: 5, neuroSections: [] };
      const neuroSections = Array.isArray(base.neuroSections) ? [newSection, ...base.neuroSections] : [newSection];
      return { ...base, neuroSections };
    });
    setTemplatesDirty(true);
  };

  const addTextResultButton = (group) => {
    const newButton = {
      id: `result_btn_${Date.now()}`,
      label: "Новая кнопка",
      type: "action",
      value: "",
      active: true
    };
    setTemplatesConfig((prev) => {
      const base = prev || {
        pageSize: 5,
        prompts: [],
        neuroPageSize: 5,
        neuroSections: [],
        textResultUi: {
          primaryText: "",
          primaryButtons: [],
          secondaryText: "",
          secondaryButtons: []
        }
      };
      const textResultUi = {
        ...(base.textResultUi || {
          primaryText: "",
          primaryButtons: [],
          secondaryText: "",
          secondaryButtons: []
        })
      };
      const buttons = Array.isArray(textResultUi[group]) ? [newButton, ...textResultUi[group]] : [newButton];
      textResultUi[group] = buttons;
      return { ...base, textResultUi };
    });
    setTemplatesDirty(true);
  };

  return (
    <div className="admin-page">
      <header className="admin-hero">
        <div className="admin-hero-info">
          <p className="admin-eyebrow">Сверхум</p>
          <h1>Админ‑панель</h1>
          <div className="admin-hero-meta">
            <span>Обновлено: {formatDateTime(analyticsUpdatedAt)}</span>
            <span>Сессия: {adminToken ? "активна" : "—"}</span>
          </div>
        </div>
        <div className="admin-hero-metrics">
          <div className="hero-card">
            <div className="hero-label">Пользователи</div>
            <div className="hero-value">{formatNumber(stats.totalUsers)}</div>
            <div className="hero-note">в базе</div>
          </div>
          <div className="hero-card">
            <div className="hero-label">Активные 24ч</div>
            <div className="hero-value">{formatNumber(stats.dailyActive)}</div>
            <div className="hero-note">уникальных</div>
          </div>
          <div className="hero-card accent">
            <div className="hero-label">Премиум</div>
            <div className="hero-value">{formatNumber(premiumTotal)}</div>
            <div className="hero-note">активных</div>
          </div>
          <div className="hero-card">
            <div className="hero-label">Доход</div>
            <div className="hero-value">{formatMoney(revenueTotal)}</div>
            <div className="hero-note">оценка</div>
          </div>
        </div>
      </header>

      <div className="admin-layout">
        <aside className="admin-nav">
          <div className="admin-nav-title">Разделы</div>
          <a href="#analytics">Аналитика</a>
          <a href="#stats">Статистика</a>
          <a href="#users">Пользователи</a>
          <a href="#mailing">Рассылка</a>
          <a href="#locales">Локали</a>
          <a href="#roulette">Рулетка</a>
          <a href="#photosession">Фотосессия</a>
          <a href="#templates">Нейросеть</a>
          <a href="#logs-by-id">Лог по ID</a>
          <a href="#logs-by-user">Логи пользователя</a>
          <a href="#logs-chart">График логов</a>
          <a href="#logs-last">Последние логи</a>
        </aside>

        <main className="admin-content">

      <section id="overview" className="panel admin-overview-panel">
        <div className="section-header">
          <h2>Обзор</h2>
          <button type="button" className="section-toggle" onClick={() => toggleSection("overview")}>
            {sectionsOpen.overview ? "Свернуть" : "Развернуть"}
          </button>
        </div>
        {sectionsOpen.overview && (
          <div className="overview-grid">
            <div className="overview-card">
              <div className="overview-card-title">С чего начать</div>
              <p>1. Откройте «Сайт и SEO» и проверьте контент главной страницы.</p>
              <p>2. Убедитесь, что блок оплаты выглядит актуально и понятно.</p>
              <p>3. После этого переходите к рассылкам, пользователям и аналитике.</p>
            </div>
            <div className="overview-card">
              <div className="overview-card-title">Быстрые переходы</div>
              <div className="overview-links">
                <a href="#site-content">Редактировать сайт</a>
                <a href="#mailing">Коммуникации</a>
                <a href="#users">Пользователи</a>
                <a href="#analytics">Аналитика</a>
              </div>
            </div>
            <div className="overview-card">
              <div className="overview-card-title">Текущее состояние</div>
              <p>Контент сайта: {siteLoading ? "загружается" : "готов к редактированию"}</p>
              <p>Несохранённые изменения: {hasUnsavedChanges ? "есть" : "нет"}</p>
              <p>Сессия администратора: {adminToken ? "активна" : "—"}</p>
            </div>
          </div>
        )}
      </section>

      <AdminSiteEditor
        config={siteConfig}
        loading={siteLoading}
        saving={siteSaving}
        dirty={siteDirty}
        tab={siteEditorTab}
        setTab={setSiteEditorTab}
        onReload={resetSiteConfig}
        onSave={saveSiteConfig}
        onChange={updateSiteConfig}
        siteBaseUrl={SITE_BASE_URL}
      />

      {/* Аналитика */}
      <section id="analytics" className="analytics-section panel">
        <div className="section-header">
          <h2>Аналитика</h2>
          <div className="section-header-actions">
            <button
              type="button"
              className="analytics-refresh"
              onClick={() => fetchAnalytics(adminToken, true)}
              disabled={analyticsLoading}
            >
              {analyticsLoading ? "Обновление..." : "Обновить"}
            </button>
            <button
              type="button"
              className="section-toggle"
              onClick={() => toggleSection("analytics")}
            >
              {sectionsOpen.analytics ? "Свернуть" : "Развернуть"}
            </button>
          </div>
        </div>
        {sectionsOpen.analytics && (
          <>
            <div className="analytics-meta">
              <span>Обновлено: {formatDateTime(analyticsData.updatedAt || analyticsData.generatedAt)}</span>
              {analyticsData.refreshing ? <span className="analytics-chip">Обновление…</span> : null}
              {analyticsData.stale ? <span className="analytics-chip warn">Данные устарели</span> : null}
            </div>
            {analyticsData.error ? (
              <div className="analytics-error">
                Ошибка аналитики: {analyticsData.error.message || "неизвестная ошибка"}
              </div>
            ) : null}

            <div className="analytics-block">
              <h3>Воронка продукта</h3>
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>Метрика</th>
                    <th>День</th>
                    <th>Неделя</th>
                    <th>Месяц</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <MetricLabel
                        title="Новые регистрации"
                        hint="Пользователи с regDate в периоде; в скобках изменение к предыдущему периоду."
                      />
                    </td>
                    <td>{formatNumber(periodDay.newUsers)} ({formatSignedPercent(periodDay.newUsersDeltaPct)})</td>
                    <td>{formatNumber(periodWeek.newUsers)} ({formatSignedPercent(periodWeek.newUsersDeltaPct)})</td>
                    <td>{formatNumber(periodMonth.newUsers)} ({formatSignedPercent(periodMonth.newUsersDeltaPct)})</td>
                  </tr>
                  <tr>
                    <td>
                      <MetricLabel
                        title="Подписка на группу"
                        hint="Из новых пользователей, у кого есть событие group_join (подписка на сообщество)."
                      />
                    </td>
                    <td>{formatNumber(periodDay.joined)} ({formatPercent(periodDay.joinRate)})</td>
                    <td>{formatNumber(periodWeek.joined)} ({formatPercent(periodWeek.joinRate)})</td>
                    <td>{formatNumber(periodMonth.joined)} ({formatPercent(periodMonth.joinRate)})</td>
                  </tr>
                  <tr>
                    <td>
                      <MetricLabel
                        title="Активировались (1+ запрос)"
                        hint="Сделали хотя бы один запрос (есть запись в chat_logs)."
                      />
                    </td>
                    <td>{formatNumber(periodDay.activation.activated)} ({formatPercent(periodDay.activation.rate)})</td>
                    <td>{formatNumber(periodWeek.activation.activated)} ({formatPercent(periodWeek.activation.rate)})</td>
                    <td>{formatNumber(periodMonth.activation.activated)} ({formatPercent(periodMonth.activation.rate)})</td>
                  </tr>
                  <tr>
                    <td>
                      <MetricLabel
                        title="Активировались за 7 дней"
                        hint="Сделали 1+ запрос в течение 7 дней после регистрации (только новые пользователи периода)."
                      />
                    </td>
                    <td>{formatNumber(periodDay.activation.activated7)} ({formatPercent(periodDay.activation.rate7)})</td>
                    <td>{formatNumber(periodWeek.activation.activated7)} ({formatPercent(periodWeek.activation.rate7)})</td>
                    <td>{formatNumber(periodMonth.activation.activated7)} ({formatPercent(periodMonth.activation.rate7)})</td>
                  </tr>
                  <tr>
                    <td>
                      <MetricLabel
                        title="Конверсия от подписавшихся"
                        hint="Активировались / Подписались на группу."
                      />
                    </td>
                    <td>{formatPercent(periodDay.activation.fromJoinedRate)}</td>
                    <td>{formatPercent(periodWeek.activation.fromJoinedRate)}</td>
                    <td>{formatPercent(periodMonth.activation.fromJoinedRate)}</td>
                  </tr>
                  <tr>
                    <td>
                      <MetricLabel
                        title="Activation time AVG (мин)"
                        hint="Среднее время от регистрации до первого запроса."
                      />
                    </td>
                    <td>{formatNumber(periodDay.activation.timeAvgMin)}</td>
                    <td>{formatNumber(periodWeek.activation.timeAvgMin)}</td>
                    <td>{formatNumber(periodMonth.activation.timeAvgMin)}</td>
                  </tr>
                  <tr>
                    <td>
                      <MetricLabel
                        title="Activation time Median (мин)"
                        hint="Медиана времени от регистрации до первого запроса."
                      />
                    </td>
                    <td>{formatNumber(periodDay.activation.timeMedianMin)}</td>
                    <td>{formatNumber(periodWeek.activation.timeMedianMin)}</td>
                    <td>{formatNumber(periodMonth.activation.timeMedianMin)}</td>
                  </tr>
                  <tr>
                    <td>
                      <MetricLabel
                        title="Activation time P50 / P75 / P90"
                        hint="Перцентили времени до первого запроса."
                      />
                    </td>
                    <td>{formatNumber(periodDay.activation.p50)} / {formatNumber(periodDay.activation.p75)} / {formatNumber(periodDay.activation.p90)}</td>
                    <td>{formatNumber(periodWeek.activation.p50)} / {formatNumber(periodWeek.activation.p75)} / {formatNumber(periodWeek.activation.p90)}</td>
                    <td>{formatNumber(periodMonth.activation.p50)} / {formatNumber(periodMonth.activation.p75)} / {formatNumber(periodMonth.activation.p90)}</td>
                  </tr>
                  <tr>
                    <td>
                      <MetricLabel
                        title="Aha (≥500 токенов/24ч)"
                        hint="Сумма токенов за первые 24ч после регистрации; если токены не пишутся — оценка по числу запросов."
                      />
                    </td>
                    <td>{formatNumber(periodDay.aha.users)} ({formatPercent(periodDay.aha.rateFromActivated)})</td>
                    <td>{formatNumber(periodWeek.aha.users)} ({formatPercent(periodWeek.aha.rateFromActivated)})</td>
                    <td>{formatNumber(periodMonth.aha.users)} ({formatPercent(periodMonth.aha.rateFromActivated)})</td>
                  </tr>
                  <tr>
                    <td>
                      <MetricLabel
                        title="Среднее запросов в 1-й день"
                        hint="Кол-во запросов в первые 24ч / новые пользователи."
                      />
                    </td>
                    <td>{formatNumber(periodDay.aha.avgRequestsDay1)}</td>
                    <td>{formatNumber(periodWeek.aha.avgRequestsDay1)}</td>
                    <td>{formatNumber(periodMonth.aha.avgRequestsDay1)}</td>
                  </tr>
                  <tr>
                    <td>
                      <MetricLabel
                        title="Доля не дошедших до Aha"
                        hint="1 − (Aha / Активировались)."
                      />
                    </td>
                    <td>{formatPercent(periodDay.aha.noAhaRate)}</td>
                    <td>{formatPercent(periodWeek.aha.noAhaRate)}</td>
                    <td>{formatPercent(periodMonth.aha.noAhaRate)}</td>
                  </tr>
                  <tr>
                    <td>
                      <MetricLabel
                        title="Смотрели тарифы"
                        hint="Пользователи с событием paywall_view; доля от Aha."
                      />
                    </td>
                    <td>{formatNumber(periodDay.paywall.viewUsers)} ({formatPercent(periodDay.paywall.rateFromAha)})</td>
                    <td>{formatNumber(periodWeek.paywall.viewUsers)} ({formatPercent(periodWeek.paywall.rateFromAha)})</td>
                    <td>{formatNumber(periodMonth.paywall.viewUsers)} ({formatPercent(periodMonth.paywall.rateFromAha)})</td>
                  </tr>
                  <tr>
                    <td>
                      <MetricLabel
                        title="Начали оплату (checkout_open)"
                        hint="Пользователи, которым была отправлена ссылка на оплату; доля от paywall_view."
                      />
                    </td>
                    <td>{formatNumber(periodDay.checkout.openUsers)} ({formatPercent(periodDay.checkout.rateFromPaywall)})</td>
                    <td>{formatNumber(periodWeek.checkout.openUsers)} ({formatPercent(periodWeek.checkout.rateFromPaywall)})</td>
                    <td>{formatNumber(periodMonth.checkout.openUsers)} ({formatPercent(periodMonth.checkout.rateFromPaywall)})</td>
                  </tr>
                  <tr>
                    <td>
                      <MetricLabel
                        title="Просмотры до покупки (avg / median)"
                        hint="Количество paywall_view до первой покупки."
                      />
                    </td>
                    <td>{formatNumber(periodDay.paywall.avgViewsBeforePurchase)} / {formatNumber(periodDay.paywall.medianViewsBeforePurchase)}</td>
                    <td>{formatNumber(periodWeek.paywall.avgViewsBeforePurchase)} / {formatNumber(periodWeek.paywall.medianViewsBeforePurchase)}</td>
                    <td>{formatNumber(periodMonth.paywall.avgViewsBeforePurchase)} / {formatNumber(periodMonth.paywall.medianViewsBeforePurchase)}</td>
                  </tr>
                  <tr>
                    <td>
                      <MetricLabel
                        title="Распределение просмотров (1 / 2–3 / 4+)"
                        hint="Сколько покупок произошло после 1 / 2–3 / 4+ просмотров тарифов."
                      />
                    </td>
                    <td>{formatNumber(periodDay.paywall.dist.one)} / {formatNumber(periodDay.paywall.dist.twoThree)} / {formatNumber(periodDay.paywall.dist.fourPlus)}</td>
                    <td>{formatNumber(periodWeek.paywall.dist.one)} / {formatNumber(periodWeek.paywall.dist.twoThree)} / {formatNumber(periodWeek.paywall.dist.fourPlus)}</td>
                    <td>{formatNumber(periodMonth.paywall.dist.one)} / {formatNumber(periodMonth.paywall.dist.twoThree)} / {formatNumber(periodMonth.paywall.dist.fourPlus)}</td>
                  </tr>
                  <tr>
                    <td>
                      <MetricLabel
                        title="Купили (уник.)"
                        hint="Пользователи с первой покупкой; CR = покупка / просмотры тарифов."
                      />
                    </td>
                    <td>{formatNumber(periodDay.purchase.users)} ({formatPercent(periodDay.purchase.crPaywallPurchase)})</td>
                    <td>{formatNumber(periodWeek.purchase.users)} ({formatPercent(periodWeek.purchase.crPaywallPurchase)})</td>
                    <td>{formatNumber(periodMonth.purchase.users)} ({formatPercent(periodMonth.purchase.crPaywallPurchase)})</td>
                  </tr>
                  <tr>
                    <td>
                      <MetricLabel
                        title="CR checkout → покупка"
                        hint="Покупка / checkout_open."
                      />
                    </td>
                    <td>{formatPercent(periodDay.purchase.crCheckoutPurchase)}</td>
                    <td>{formatPercent(periodWeek.purchase.crCheckoutPurchase)}</td>
                    <td>{formatPercent(periodMonth.purchase.crCheckoutPurchase)}</td>
                  </tr>
                  <tr>
                    <td>
                      <MetricLabel
                        title="CR Aha → покупка"
                        hint="Покупки / Aha."
                      />
                    </td>
                    <td>{formatPercent(periodDay.purchase.crAhaPurchase)}</td>
                    <td>{formatPercent(periodWeek.purchase.crAhaPurchase)}</td>
                    <td>{formatPercent(periodMonth.purchase.crAhaPurchase)}</td>
                  </tr>
                  <tr>
                    <td>
                      <MetricLabel
                        title="Покупки с Aha / без Aha"
                        hint="Сколько покупателей имеют зафиксированный Aha до покупки в окне метрики."
                      />
                    </td>
                    <td>{formatNumber(periodDay.purchase.usersWithAha)} / {formatNumber(periodDay.purchase.usersWithoutAha)}</td>
                    <td>{formatNumber(periodWeek.purchase.usersWithAha)} / {formatNumber(periodWeek.purchase.usersWithoutAha)}</td>
                    <td>{formatNumber(periodMonth.purchase.usersWithAha)} / {formatNumber(periodMonth.purchase.usersWithoutAha)}</td>
                  </tr>
                  <tr>
                    <td>
                      <MetricLabel
                        title="Aha → Purchase lag (avg / median, дней)"
                        hint="Среднее и медиана дней от Aha до первой покупки. Если нет выборки, показывается —."
                      />
                    </td>
                    <td>{formatAhaLag(periodDay.purchase)}</td>
                    <td>{formatAhaLag(periodWeek.purchase)}</td>
                    <td>{formatAhaLag(periodMonth.purchase)}</td>
                  </tr>
                  <tr>
                    <td>
                      <MetricLabel
                        title="% покупок в день 0 / 1 / 3 / 7"
                        hint="Доля покупок, случившихся в пределах N дней после Aha (накопительно). Если нет выборки, показывается —."
                      />
                    </td>
                    <td>{formatAhaLagBuckets(periodDay.purchase)}</td>
                    <td>{formatAhaLagBuckets(periodWeek.purchase)}</td>
                    <td>{formatAhaLagBuckets(periodMonth.purchase)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="analytics-block">
              <h3>Drop-off анализ</h3>
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>
                      <MetricLabel title="Этап" hint="Шаги воронки продукта." />
                    </th>
                    <th>
                      <MetricLabel title="Количество" hint="Абсолютное число пользователей на этапе." />
                    </th>
                    <th>
                      <MetricLabel title="% от предыдущего" hint="Текущий / предыдущий этап." />
                    </th>
                    <th>
                      <MetricLabel title="Потеря" hint="Предыдущий − текущий (в % от предыдущего)." />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {dropOff.steps.map((step, index) => (
                    <tr key={step.key || index}>
                      <td>{step.label}</td>
                      <td>{formatNumber(step.value)}</td>
                      <td>{index === 0 ? "—" : formatPercent(step.percentFromPrev)}</td>
                      <td>{index === 0 ? "—" : `${formatNumber(step.drop)} (${formatPercent(step.dropRate)})`}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {dropOff.bottleneck ? (
                <div className="analytics-note">
                  Основной вклад в потери: {dropOff.bottleneck.label} (−{formatNumber(dropOff.bottleneck.drop)}).
                </div>
              ) : null}
            </div>

            <div className="analytics-block">
              <h3>Деньги</h3>
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>Метрика</th>
                    <th>День</th>
                    <th>Неделя</th>
                    <th>Месяц</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <MetricLabel title="Выручка" hint="Сумма покупок (amount) за период." />
                    </td>
                    <td>{formatMoney(moneyDay.revenue)}</td>
                    <td>{formatMoney(moneyWeek.revenue)}</td>
                    <td>{formatMoney(moneyMonth.revenue)}</td>
                  </tr>
                  <tr>
                    <td>
                      <MetricLabel title="ARPPU" hint="Выручка / уникальные платящие пользователи." />
                    </td>
                    <td>{formatMoney(moneyDay.arppu)}</td>
                    <td>{formatMoney(moneyWeek.arppu)}</td>
                    <td>{formatMoney(moneyMonth.arppu)}</td>
                  </tr>
                  <tr>
                    <td>
                      <MetricLabel title="VK Donut" hint="Выручка с source=vkdonut." />
                    </td>
                    <td>{formatMoney(moneyDay.bySource.vkdonut)}</td>
                    <td>{formatMoney(moneyWeek.bySource.vkdonut)}</td>
                    <td>{formatMoney(moneyMonth.bySource.vkdonut)}</td>
                  </tr>
                  <tr>
                    <td>
                      <MetricLabel title="ЮKassa" hint="Выручка с source=yookassa." />
                    </td>
                    <td>{formatMoney(moneyDay.bySource.yookassa)}</td>
                    <td>{formatMoney(moneyWeek.bySource.yookassa)}</td>
                    <td>{formatMoney(moneyMonth.bySource.yookassa)}</td>
                  </tr>
                  <tr>
                    <td>
                      <MetricLabel title="Продления" hint="Покупки с kind=renewal/prolonged." />
                    </td>
                    <td>{formatNumber(moneyDay.renewals)}</td>
                    <td>{formatNumber(moneyWeek.renewals)}</td>
                    <td>{formatNumber(moneyMonth.renewals)}</td>
                  </tr>
                  <tr>
                    <td>
                      <MetricLabel title="Подписки (новые) — уник. / выручка" hint="Новые подписки (уникальные пользователи и выручка)." />
                    </td>
                    <td>{formatNumber(moneyDay.subscriptions.new.users)} ({formatMoney(moneyDay.subscriptions.new.revenue)})</td>
                    <td>{formatNumber(moneyWeek.subscriptions.new.users)} ({formatMoney(moneyWeek.subscriptions.new.revenue)})</td>
                    <td>{formatNumber(moneyMonth.subscriptions.new.users)} ({formatMoney(moneyMonth.subscriptions.new.revenue)})</td>
                  </tr>
                  <tr>
                    <td>
                      <MetricLabel title="Подписки (продления) — уник. / выручка" hint="Продления подписок (уникальные пользователи и выручка)." />
                    </td>
                    <td>{formatNumber(moneyDay.subscriptions.renewals.users)} ({formatMoney(moneyDay.subscriptions.renewals.revenue)})</td>
                    <td>{formatNumber(moneyWeek.subscriptions.renewals.users)} ({formatMoney(moneyWeek.subscriptions.renewals.revenue)})</td>
                    <td>{formatNumber(moneyMonth.subscriptions.renewals.users)} ({formatMoney(moneyMonth.subscriptions.renewals.revenue)})</td>
                  </tr>
                  <tr>
                    <td>
                      <MetricLabel title="Подписки (всего) — уник. / выручка" hint="Все подписки (уникальные пользователи и выручка)." />
                    </td>
                    <td>{formatNumber(moneyDay.subscriptions.total.users)} ({formatMoney(moneyDay.subscriptions.total.revenue)})</td>
                    <td>{formatNumber(moneyWeek.subscriptions.total.users)} ({formatMoney(moneyWeek.subscriptions.total.revenue)})</td>
                    <td>{formatNumber(moneyMonth.subscriptions.total.users)} ({formatMoney(moneyMonth.subscriptions.total.revenue)})</td>
                  </tr>
                  <tr>
                    <td>
                      <MetricLabel title="Разовые покупки — уник. / выручка" hint="Покупки без автосписания (уникальные пользователи и выручка)." />
                    </td>
                    <td>{formatNumber(moneyDay.oneTime.users)} ({formatMoney(moneyDay.oneTime.revenue)})</td>
                    <td>{formatNumber(moneyWeek.oneTime.users)} ({formatMoney(moneyWeek.oneTime.revenue)})</td>
                    <td>{formatNumber(moneyMonth.oneTime.users)} ({formatMoney(moneyMonth.oneTime.revenue)})</td>
                  </tr>
                  <tr>
                    <td>
                      <MetricLabel title="Ошибки оплаты" hint="payment_fail (в т.ч. авто‑продления)" />
                    </td>
                    <td>{formatNumber(moneyDay.paymentFails)}</td>
                    <td>{formatNumber(moneyWeek.paymentFails)}</td>
                    <td>{formatNumber(moneyMonth.paymentFails)}</td>
                  </tr>
                  <tr>
                    <td>
                      <MetricLabel title="Отмены подписок" hint="donut_subscription_cancelled + payment_fail (для аналитики отмен)" />
                    </td>
                    <td>{formatNumber(moneyDay.subscriptionCancels)}</td>
                    <td>{formatNumber(moneyWeek.subscriptionCancels)}</td>
                    <td>{formatNumber(moneyMonth.subscriptionCancels)}</td>
                  </tr>
                  <tr>
                    <td>
                      <MetricLabel title="Маржа" hint="(Выручка − себестоимость токенов) / выручка." />
                    </td>
                    <td>{formatPercent(moneyDay.margin)}</td>
                    <td>{formatPercent(moneyWeek.margin)}</td>
                    <td>{formatPercent(moneyMonth.margin)}</td>
                  </tr>
                </tbody>
              </table>
              <div className="analytics-kpis">
                <MetricKpi
                  title="Новые платящие за месяц"
                  value={formatNumber(analyticsData.money.newPayersMonth)}
                  hint="Пользователи с первой покупкой за последние 30 дней."
                />
                <MetricKpi
                  title="Активные подписки"
                  value={formatNumber(analyticsData.money.activePaid)}
                  hint="Пользователи с premium_id > 0 (согласовано с разделом «Статистика»)."
                />
                <MetricKpi
                  title="Среднее время до первой покупки"
                  value={`${formatNumber(analyticsData.money.avgTimeToFirstPurchaseDays)} дн`}
                  hint="Среднее дней от регистрации до первой покупки."
                />
                <MetricKpi
                  title="Доля выручки от старых пользователей"
                  value={formatPercent(analyticsData.money.revenueFromOldUsersShare)}
                  hint="Выручка от пользователей, зарегистрированных до начала 30-дневного окна."
                />
              </div>
              <div className="analytics-kpis">
                <MetricKpi
                  title="Lite"
                  value={`${formatNumber(activeByPlan.lite || moneyMonth.byPlan.lite.count)} (${formatMoney(moneyMonth.byPlan.lite.revenue)})`}
                  hint="Активные подписки Lite / выручка по Lite за 30 дней."
                />
                <MetricKpi
                  title="Pro"
                  value={`${formatNumber(activeByPlan.pro || moneyMonth.byPlan.pro.count)} (${formatMoney(moneyMonth.byPlan.pro.revenue)})`}
                  hint="Активные подписки Pro / выручка по Pro за 30 дней."
                />
                <MetricKpi
                  title="Max"
                  value={`${formatNumber(activeByPlan.max || moneyMonth.byPlan.max.count)} (${formatMoney(moneyMonth.byPlan.max.revenue)})`}
                  hint="Активные подписки Max / выручка по Max за 30 дней."
                />
              </div>
              <div className="analytics-kpis">
                <MetricKpi
                  title="Доля Lite"
                  value={formatShare(moneyMonth.byPlan.lite.revenue, moneyMonth.revenue)}
                  hint="Выручка Lite / вся выручка месяца."
                />
                <MetricKpi
                  title="Доля Pro"
                  value={formatShare(moneyMonth.byPlan.pro.revenue, moneyMonth.revenue)}
                  hint="Выручка Pro / вся выручка месяца."
                />
                <MetricKpi
                  title="Доля Max"
                  value={formatShare(moneyMonth.byPlan.max.revenue, moneyMonth.revenue)}
                  hint="Выручка Max / вся выручка месяца."
                />
              </div>
            </div>

            <div className="analytics-block">
              <h3>Продажи по продуктам</h3>
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>Продукт</th>
                    <th>День</th>
                    <th>Неделя</th>
                    <th>Месяц</th>
                  </tr>
                </thead>
                <tbody>
                  {PRODUCT_GROUP_LABELS.map((group) => (
                    <tr key={`product-${group.key}`}>
                      <td>{group.label}</td>
                      <td>{formatProductCell(moneyDay, group.key)}</td>
                      <td>{formatProductCell(moneyWeek, group.key)}</td>
                      <td>{formatProductCell(moneyMonth, group.key)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="analytics-note">
                Количество покупок и выручка по группам продуктов.
              </div>
            </div>

            <div className="analytics-block">
              <h3>Удержание и ценность</h3>
              <table className="analytics-table">
                <tbody>
                  <tr>
                    <td>
                      <MetricLabel
                        title="Retention D1 / D7 / D30"
                        hint="Доля пользователей, активных на день 1/7/30 после регистрации (rolling по тем, кому уже ≥N дней)."
                      />
                    </td>
                    <td>{formatPercent(retention.d1)} / {formatPercent(retention.d7)} / {formatPercent(retention.d30)}</td>
                  </tr>
                  <tr>
                    <td>
                      <MetricLabel
                        title="Повторные покупки"
                        hint="Пользователи с ≥2 покупками; в скобках доля от всех платящих."
                      />
                    </td>
                    <td>{formatNumber(analyticsData.money.repeatPurchaseCount)} ({formatPercent(analyticsData.money.repeatPurchaseRate)})</td>
                  </tr>
                  <tr>
                    <td>
                      <MetricLabel
                        title="% продления после 1 месяца"
                        hint="Доля платящих, у кого между первой и последней покупкой ≥30 дней."
                      />
                    </td>
                    <td>{formatPercent(analyticsData.money.renewRate)}</td>
                  </tr>
                  <tr>
                    <td>
                      <MetricLabel
                        title="LTV"
                        hint="ARPPU × среднее число покупок/месяцев."
                      />
                    </td>
                    <td>{formatMoney(analyticsData.money.ltv)}</td>
                  </tr>
                  <tr>
                    <td>
                      <MetricLabel
                        title="CAC"
                        hint="Маркетинг за месяц / новые платящие за месяц."
                      />
                    </td>
                    <td>{formatMoney(analyticsData.money.cac)}</td>
                  </tr>
                  <tr>
                    <td>
                      <MetricLabel
                        title="LTV / CAC"
                        hint="Отношение LTV к CAC."
                      />
                    </td>
                    <td>{formatNumber(analyticsData.money.ltvToCac)}</td>
                  </tr>
                  <tr>
                    <td>
                      <MetricLabel
                        title="Средний цикл жизни (дней)"
                        hint="Среднее дней между первой и последней покупкой (для пользователей с ≥2 покупками)."
                      />
                    </td>
                    <td>{formatNumber(analyticsData.money.lifecycleDays)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="analytics-block">
              <h3>Новые метрики (неделя)</h3>
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>Метрика</th>
                    <th>Значение</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <MetricLabel title="Конверсия в оплату: подписка (новая)" hint="Пользователи + выручка + CR от новых за неделю." />
                    </td>
                    <td>
                      {formatNumber(weekly.purchaseTypes.subscriptionNew.users)} / {formatMoney(weekly.purchaseTypes.subscriptionNew.revenue)} ({formatPercent(weekly.purchaseTypes.subscriptionNew.rate)})
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <MetricLabel title="Конверсия в оплату: разовые" hint="Пользователи + выручка + CR от новых за неделю." />
                    </td>
                    <td>
                      {formatNumber(weekly.purchaseTypes.oneTime.users)} / {formatMoney(weekly.purchaseTypes.oneTime.revenue)} ({formatPercent(weekly.purchaseTypes.oneTime.rate)})
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <MetricLabel title="Разовые → подписка (7 дней)" hint="Доля разовых покупателей, купивших подписку за 7 дней." />
                    </td>
                    <td>
                      {formatNumber(weekly.oneTimeToSubscription.converted7)} ({formatPercent(weekly.oneTimeToSubscription.rate7)})
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <MetricLabel title="Разовые → подписка (30 дней)" hint="Доля разовых покупателей, купивших подписку за 30 дней." />
                    </td>
                    <td>
                      {formatNumber(weekly.oneTimeToSubscription.converted30)} ({formatPercent(weekly.oneTimeToSubscription.rate30)})
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <MetricLabel title="Подписки (неделя): новые / продления" hint="Количество уникальных пользователей." />
                    </td>
                    <td>
                      {formatNumber(weekly.subscriptions.new.users)} / {formatNumber(weekly.subscriptions.renewals.users)}
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <MetricLabel title="Подписки (неделя): отмены / чистый прирост" hint="Отмены = donut_subscription_cancelled + payment_fail." />
                    </td>
                    <td>
                      {formatNumber(weekly.subscriptions.cancellations)} / {formatNumber(weekly.subscriptions.net)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="analytics-block">
              <h3>Entry point в оплате (неделя)</h3>
              {weekly.entryPoints?.rows?.length ? (
                <table className="analytics-table">
                  <thead>
                    <tr>
                      <th>Entry point</th>
                      <th>Paywall users</th>
                      <th>Purchase users</th>
                      <th>CR paywall→purchase</th>
                      <th>Покупок</th>
                      <th>Выручка</th>
                      <th>Средний чек</th>
                    </tr>
                  </thead>
                  <tbody>
                    {weekly.entryPoints.rows.map((row, index) => (
                      <tr key={`entry-${row.entryPoint || "unknown"}-${index}`}>
                        <td>{formatEntryPoint(row.entryPoint)}</td>
                        <td>{formatNumber(row.paywallUsers)}</td>
                        <td>{formatNumber(row.purchaseUsers)}</td>
                        <td>{formatPercent(row.conversion)}</td>
                        <td>{formatNumber(row.purchases)}</td>
                        <td>{formatMoney(row.revenue)}</td>
                        <td>{formatMoney(row.avgCheck)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="analytics-note">Нет данных по entry_point.</div>
              )}
            </div>

            <div className="analytics-block">
              <h3>Роли и оплата (неделя)</h3>
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>Роль</th>
                    <th>Платящие (шт / %)</th>
                    <th>Выручка подписки</th>
                    <th>Выручка разовые</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Школьники</td>
                    <td>{formatNumber(weekly.roles.school.payers)} ({formatPercent(weekly.roles.school.payRate)})</td>
                    <td>{formatMoney(weekly.roles.school.subsRevenue)}</td>
                    <td>{formatMoney(weekly.roles.school.oneTimeRevenue)}</td>
                  </tr>
                  <tr>
                    <td>Другое</td>
                    <td>{formatNumber(weekly.roles.other.payers)} ({formatPercent(weekly.roles.other.payRate)})</td>
                    <td>{formatMoney(weekly.roles.other.subsRevenue)}</td>
                    <td>{formatMoney(weekly.roles.other.oneTimeRevenue)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="analytics-block">
              <h3>Когорты</h3>
              {cohorts.rows.length > 0 ? (
                <table className="analytics-table">
                  <thead>
                    <tr>
                      <th>
                        <MetricLabel title="Когорта (неделя)" hint="Неделя регистрации (понедельник)." />
                      </th>
                      <th>
                        <MetricLabel title="Пользователи" hint="Размер когорты." />
                      </th>
                      <th>
                        <MetricLabel title="Retention D1" hint="Доля когорты, активная на день 1." />
                      </th>
                      <th>
                        <MetricLabel title="Retention D7" hint="Доля когорты, активная на день 7." />
                      </th>
                      <th>
                        <MetricLabel title="Retention D30" hint="Доля когорты, активная на день 30." />
                      </th>
                      <th>
                        <MetricLabel title="Aha %" hint="Доля когорты, достигших Aha." />
                      </th>
                      <th>
                        <MetricLabel title="Покупка %" hint="Доля когорты, совершивших покупку." />
                      </th>
                      <th>
                        <MetricLabel title="ARPPU" hint="Выручка / платящие пользователи когорты." />
                      </th>
                      <th>
                        <MetricLabel title="LTV" hint="ARPPU × среднее число покупок." />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {cohorts.rows.map((row) => (
                      <tr key={row.cohort}>
                        <td>{row.cohort}</td>
                        <td>{formatNumber(row.size)}</td>
                        <td>{formatPercent(row.retentionD1)}</td>
                        <td>{formatPercent(row.retentionD7)}</td>
                        <td>{formatPercent(row.retentionD30)}</td>
                        <td>{formatPercent(row.ahaRate)}</td>
                        <td>{formatPercent(row.purchaseRate)}</td>
                        <td>{formatMoney(row.arppu)}</td>
                        <td>{formatMoney(row.ltv)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="analytics-note">Нет данных для когорт.</div>
              )}
              {cohorts.insights?.length ? (
                <div className="analytics-note">{cohorts.insights.join(" ")}</div>
              ) : null}
            </div>

            <div className="analytics-block">
              <h3>Роли пользователей</h3>
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>
                      <MetricLabel title="Роль" hint="Самоопределение при первом входе." />
                    </th>
                    <th>
                      <MetricLabel title="Новые за 30 дней" hint="Количество и доля новых пользователей с этой ролью." />
                    </th>
                    <th>
                      <MetricLabel title="Платящие за 30 дней" hint="Количество и доля платящих с этой ролью." />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {[0, 1, 2, 3, 4].map((roleId) => (
                    <tr key={`role-${roleId}`}>
                      <td>{({
                        0: "Не выбрали",
                        1: "🎓 Воспитатель ДОУ",
                        2: "📚 Учитель СОШ",
                        3: "👩‍💼 Методист/завуч",
                        4: "🤷 Другое"
                      }[roleId] || ROLE_LABELS[roleId])}</td>
                      <td>{formatNumber(roles.newUsers.roles?.[roleId] || 0)} ({formatPercent(roles.newUsers.shares?.[roleId] || 0)})</td>
                      <td>{formatNumber(roles.payers.roles?.[roleId] || 0)} ({formatPercent(roles.payers.shares?.[roleId] || 0)})</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="analytics-block">
              <h3>Аналитические выводы</h3>
              {analyticsData.executiveSummary?.length ? (
                <ul className="analytics-list">
                  {analyticsData.executiveSummary.map((item, index) => (
                    <li key={`summary-${index}`}>{item}</li>
                  ))}
                </ul>
              ) : (
                <div className="analytics-note">Выводы появятся после накопления данных.</div>
              )}
              <div className="analytics-kpis">
                <MetricKpi
                  title="Главный bottleneck"
                  value={conclusions.bottleneck?.label || "—"}
                  hint="Максимальная потеря между этапами воронки."
                />
                <MetricKpi
                  title="Метрика роста"
                  value={conclusions.growthMetric || "—"}
                  hint="Самая слабая метрика из ключевых этапов воронки."
                />
                <MetricKpi
                  title="Фокус улучшений"
                  value={conclusions.focus || "—"}
                  hint="Приоритетная зона роста по текущим данным."
                />
                <MetricKpi
                  title="Прогноз до цели 300"
                  value={conclusions.goalWeeks ? `${conclusions.goalWeeks} недель` : "—"}
                  hint="Оценка времени до 300 активных подписок при текущем темпе."
                />
              </div>
            </div>

            <div className="analytics-block">
              <h3>Ежедневный дашборд</h3>
              <pre className="analytics-dashboard">{dashboard.text || "Нет данных"}</pre>
            </div>
          </>
        )}
      </section>

      {/* Локализация */}
      <section id="locales" className="locales-section panel">
        <div className="section-header">
          <h2>Тексты локалей</h2>
          <div className="section-header-actions">
            <button
              type="button"
              className="locales-btn"
              onClick={() => fetchLocaleData(adminToken, localeLang)}
              disabled={!adminToken || localeSaving}
            >
              Обновить
            </button>
            <button
              type="button"
              className="locales-btn primary"
              onClick={handleSaveLocales}
              disabled={!localeDirty || localeSaving}
            >
              {localeSaving ? "Сохранение..." : "Сохранить"}
            </button>
            <button
              type="button"
              className="section-toggle"
              onClick={() => toggleSection("locales")}
            >
              {sectionsOpen.locales ? "Свернуть" : "Развернуть"}
            </button>
          </div>
        </div>

        {sectionsOpen.locales && (
          <>
            <div className="locales-subtitle">
              Осторожно с плейсхолдерами вида {"{name}"} — они используются в тексте.
            </div>

            <div className="locales-controls">
              <select
                value={localeLang}
                onChange={(e) => setLocaleLang(e.target.value)}
                disabled={!adminToken}
              >
                {localesList.length > 0 ? (
                  localesList.map((lang) => (
                    <option key={lang} value={lang}>{lang}</option>
                  ))
                ) : (
                  <option value="ru">ru</option>
                )}
              </select>
              <input
                type="text"
                placeholder="Поиск по ключу или тексту"
                value={localeFilter}
                onChange={(e) => setLocaleFilter(e.target.value)}
              />
              <div className="locales-count">
                {filteredLocales.length} / {localeEntries.length}
              </div>
            </div>

            <div className="locales-list">
              {filteredLocales.map(([key, value]) => (
                <div className="locale-item" key={key}>
                  <div className="locale-key">{key}</div>
                  <textarea
                    rows={3}
                    value={value ?? ""}
                    onChange={(e) => {
                      const nextValue = e.target.value;
                      setLocaleData((prev) => ({ ...prev, [key]: nextValue }));
                      setLocaleDirty(true);
                    }}
                  />
                </div>
              ))}
            </div>
          </>
        )}
      </section>

      {/* Рулетка */}
      <section id="roulette" className="roulette-section panel">
        <div className="section-header">
          <h2>Рулетка — призы</h2>
          <div className="section-header-actions">
            <button
              type="button"
              className="roulette-btn"
              onClick={() => fetchRouletteConfig(adminToken)}
              disabled={rouletteLoading}
            >
              {rouletteLoading ? "Обновление..." : "Обновить"}
            </button>
            <button
              type="button"
              className="roulette-btn primary"
              onClick={handleSaveRoulette}
              disabled={!rouletteDirty || rouletteSaving}
            >
              {rouletteSaving ? "Сохранение..." : "Сохранить"}
            </button>
            <button
              type="button"
              className="section-toggle"
              onClick={() => toggleSection("roulette")}
            >
              {sectionsOpen.roulette ? "Свернуть" : "Развернуть"}
            </button>
          </div>
        </div>

        {sectionsOpen.roulette && (
          <>
            <div className="roulette-controls">
              <div className="roulette-tabs">
                <button
                  type="button"
                  className={`roulette-tab ${rouletteTab === "free" ? "active" : ""}`}
                  onClick={() => setRouletteTab("free")}
                >
                  Free
                </button>
                <button
                  type="button"
                  className={`roulette-tab ${rouletteTab === "paid" ? "active" : ""}`}
                  onClick={() => setRouletteTab("paid")}
                >
                  Paid
                </button>
              </div>
              <div className="roulette-actions">
                <button type="button" className="roulette-btn" onClick={addRoulettePrize}>
                  Добавить приз
                </button>
              </div>
            </div>
            <datalist id="roulette-accent-presets">
              {ROULETTE_ACCENT_PRESETS.map((color) => (
                <option key={color} value={color} />
              ))}
            </datalist>

            <div className="roulette-list">
              {getRoulettePrizes().map((prize, index) => (
                <div className="roulette-item" key={`prize-${rouletteTab}-${index}`}>
                  <div className="roulette-row">
                    <label className="roulette-field">
                      <span>ID</span>
                      <input
                        className="roulette-input"
                        value={prize.id || ""}
                        onChange={(e) => updateRoulettePrize(index, { id: e.target.value })}
                      />
                    </label>
                    <label className="roulette-field">
                      <span>Название</span>
                      <input
                        className="roulette-input"
                        value={prize.label || ""}
                        onChange={(e) => updateRoulettePrize(index, { label: e.target.value })}
                      />
                    </label>
                    <label className="roulette-field">
                      <span>Тип</span>
                      <select
                        className="roulette-input"
                        value={prize.type || "tokens"}
                        onChange={(e) => updateRoulettePrize(index, { type: e.target.value })}
                      >
                        <option value="tokens">tokens</option>
                        <option value="discount">discount</option>
                        <option value="subscription">subscription</option>
                        <option value="money">money</option>
                        <option value="sticker">sticker</option>
                        <option value="text">text</option>
                      </select>
                    </label>
                  </div>

                  <div className="roulette-row">
                    <label className="roulette-field">
                      <span>Вес</span>
                      <input
                        className="roulette-input"
                        type="number"
                        value={prize.weight ?? 0}
                        onChange={(e) => updateRoulettePrize(index, { weight: Number(e.target.value || 0) })}
                      />
                    </label>
                    <label className="roulette-field">
                      <span>Редкость</span>
                      <select
                        className="roulette-input"
                        value={prize.rarity || "common"}
                        onChange={(e) => updateRoulettePrize(index, { rarity: e.target.value })}
                      >
                        <option value="common">common</option>
                        <option value="rare">rare</option>
                        <option value="epic">epic</option>
                        <option value="legendary">legendary</option>
                      </select>
                    </label>
                    <label className="roulette-field">
                      <span>Иконка</span>
                      <input
                        className="roulette-input"
                        value={prize.icon || ""}
                        onChange={(e) => updateRoulettePrize(index, { icon: e.target.value })}
                      />
                      <div className="icon-presets">
                        {ROULETTE_ICON_PRESETS.map((icon) => (
                          <button
                            key={`${icon}-${index}`}
                            type="button"
                            className={`icon-chip ${prize.icon === icon ? "active" : ""}`}
                            onClick={() => updateRoulettePrize(index, { icon })}
                            title={icon}
                          >
                            <img src={icon} alt="" />
                          </button>
                        ))}
                      </div>
                    </label>
                    <label className="roulette-field">
                      <span>Акцент</span>
                      <input
                        className="roulette-input"
                        value={prize.accent || ""}
                        onChange={(e) => updateRoulettePrize(index, { accent: e.target.value })}
                        list="roulette-accent-presets"
                      />
                      <div className="accent-presets">
                        {ROULETTE_ACCENT_PRESETS.map((color) => (
                          <button
                            key={`${color}-${index}`}
                            type="button"
                            className="accent-chip"
                            style={{ backgroundColor: color }}
                            title={color}
                            onClick={() => updateRoulettePrize(index, { accent: color })}
                          />
                        ))}
                      </div>
                    </label>
                  </div>

                  <div className="roulette-row">
                    {["tokens", "discount", "money"].includes(prize.type) && (
                      <label className="roulette-field">
                        <span>Значение</span>
                        <input
                          className="roulette-input"
                          type="number"
                          value={prize.value ?? 0}
                          onChange={(e) => updateRoulettePrize(index, { value: Number(e.target.value || 0) })}
                        />
                      </label>
                    )}
                    {prize.type === "subscription" && (
                      <>
                        <label className="roulette-field">
                          <span>Значение</span>
                          <input
                            className="roulette-input"
                            type="number"
                            value={prize.value ?? 0}
                            onChange={(e) => updateRoulettePrize(index, { value: Number(e.target.value || 0) })}
                          />
                        </label>
                        <label className="roulette-field">
                          <span>План</span>
                          <input
                            className="roulette-input"
                            type="number"
                            value={prize.plan ?? 0}
                            onChange={(e) => updateRoulettePrize(index, { plan: Number(e.target.value || 0) })}
                          />
                        </label>
                        <label className="roulette-field">
                          <span>Дни</span>
                          <input
                            className="roulette-input"
                            type="number"
                            value={prize.days ?? 0}
                            onChange={(e) => updateRoulettePrize(index, { days: Number(e.target.value || 0) })}
                          />
                        </label>
                      </>
                    )}
                    <button
                      type="button"
                      className="roulette-remove"
                      onClick={() => removeRoulettePrize(index)}
                    >
                      Удалить
                    </button>
                  </div>

                  {["text", "sticker"].includes(prize.type) ? (
                    <div className="roulette-row">
                      <label className="roulette-field full">
                        <span>Текст сообщения пользователю</span>
                        <textarea
                          className="roulette-textarea"
                          rows={3}
                          value={prize.message || ""}
                          onChange={(e) => updateRoulettePrize(index, { message: e.target.value })}
                        />
                      </label>
                    </div>
                  ) : null}
                </div>
              ))}
              {getRoulettePrizes().length === 0 && (
                <div className="roulette-empty">Призы не найдены.</div>
              )}
            </div>
          </>
        )}
      </section>

      {/* ИИ фотосессия */}
      <section id="photosession" className="roulette-section photosession-section panel">
        <div className="section-header">
          <h2>ИИ фотосессия — стили</h2>
          <div className="section-header-actions">
            <button
              type="button"
              className="roulette-btn"
              onClick={() => fetchPhotosessionConfig(adminToken)}
              disabled={photosessionLoading}
            >
              {photosessionLoading ? "Обновление..." : "Обновить"}
            </button>
            <button
              type="button"
              className="roulette-btn primary"
              onClick={handleSavePhotosession}
              disabled={!photosessionDirty || photosessionSaving}
            >
              {photosessionSaving ? "Сохранение..." : "Сохранить"}
            </button>
            <button
              type="button"
              className="section-toggle"
              onClick={() => toggleSection("photosession")}
            >
              {sectionsOpen.photosession ? "Свернуть" : "Развернуть"}
            </button>
          </div>
        </div>

        {sectionsOpen.photosession && (
          <>
            <div className="roulette-row">
              <label className="roulette-field">
                <span>Картинок на страницу</span>
                <input
                  className="roulette-input"
                  type="number"
                  min="1"
                  max="10"
                  value={Number(photosessionConfig?.pageSize || 5)}
                  onChange={(e) => updatePhotosessionConfig({ pageSize: Number(e.target.value || 5) })}
                />
              </label>
              <label className="roulette-field">
                <span>Цена в токенах</span>
                <input
                  className="roulette-input"
                  type="number"
                  min="1"
                  value={Number(photosessionConfig?.tokenCost || 8000)}
                  onChange={(e) => updatePhotosessionConfig({ tokenCost: Number(e.target.value || 8000) })}
                />
              </label>
              <label className="roulette-field">
                <span>ID продукта (29₽)</span>
                <input
                  className="roulette-input"
                  type="number"
                  min="1"
                  value={Number(photosessionConfig?.oneTimeProductId || 9)}
                  onChange={(e) => updatePhotosessionConfig({ oneTimeProductId: Number(e.target.value || 9) })}
                />
              </label>
            </div>

            <div className="roulette-actions" style={{ marginBottom: 12 }}>
              <button type="button" className="roulette-btn" onClick={addPhotosessionStyle}>
                Добавить стиль
              </button>
            </div>

            <div className="roulette-list">
              {getPhotosessionStyles().map((style, index) => (
                <div className="roulette-item" key={`photosession-style-${index}`}>
                  <div className="roulette-row">
                    <label className="roulette-field">
                      <span>ID</span>
                      <input
                        className="roulette-input"
                        value={style.id || ""}
                        onChange={(e) => updatePhotosessionStyle(index, { id: e.target.value })}
                      />
                    </label>
                    <label className="roulette-field">
                      <span>Эмодзи</span>
                      <input
                        className="roulette-input"
                        value={`${style.emoji || "✨"} (авто)`}
                        readOnly
                      />
                    </label>
                    <label className="roulette-field">
                      <span>Название</span>
                      <input
                        className="roulette-input"
                        value={style.title || ""}
                        onChange={(e) => updatePhotosessionStyle(index, { title: e.target.value })}
                      />
                    </label>
                    <label className="roulette-field">
                      <span>Активен</span>
                      <select
                        className="roulette-input"
                        value={style.active === false ? "0" : "1"}
                        onChange={(e) => updatePhotosessionStyle(index, { active: e.target.value === "1" })}
                      >
                        <option value="1">Да</option>
                        <option value="0">Нет</option>
                      </select>
                    </label>
                    <label className="roulette-field">
                      <span>Лайки</span>
                      <input
                        className="roulette-input"
                        type="number"
                        min="0"
                        value={Number(style.likes || 0)}
                        onChange={(e) => updatePhotosessionStyle(index, { likes: Math.max(0, Number(e.target.value || 0)) })}
                      />
                    </label>
                    <label className="roulette-field">
                      <span>Дизлайки</span>
                      <input
                        className="roulette-input"
                        type="number"
                        min="0"
                        value={Number(style.dislikes || 0)}
                        onChange={(e) => updatePhotosessionStyle(index, { dislikes: Math.max(0, Number(e.target.value || 0)) })}
                      />
                    </label>
                  </div>

                  <div className="roulette-row">
                    <label className="roulette-field full">
                      <span>Превью (attachment photo-...)</span>
                      <input
                        className="roulette-input"
                        value={style.previewAttachment || ""}
                        onChange={(e) => updatePhotosessionStyle(index, { previewAttachment: e.target.value })}
                      />
                    </label>
                  </div>

                  <div className="roulette-row">
                    <label className="roulette-field">
                      <span>Автозагрузка превью</span>
                      <input
                        className="roulette-input"
                        type="file"
                        accept="image/*"
                        disabled={photosessionUploadingIndex === index}
                        onChange={(e) => {
                          const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                          if (file) {
                            handlePhotosessionPreviewUpload(index, file);
                          }
                          e.target.value = "";
                        }}
                      />
                    </label>
                    <label className="roulette-field full">
                      <span>Статус</span>
                      <input
                        className="roulette-input"
                        readOnly
                        value={
                          photosessionUploadingIndex === index
                            ? "Загрузка..."
                            : style.previewAttachment || "Превью не загружено"
                        }
                      />
                    </label>
                  </div>

                  <div className="roulette-row">
                    <label className="roulette-field full">
                      <span>Prompt</span>
                      <textarea
                        className="roulette-textarea"
                        rows={4}
                        value={style.prompt || ""}
                        onChange={(e) => updatePhotosessionStyle(index, { prompt: e.target.value })}
                      />
                    </label>
                  </div>

                  <div className="mailing-button-controls">
                    <button
                      type="button"
                      onClick={() => movePhotosessionStyle(index, "up")}
                      disabled={index === 0}
                    >
                      Вверх
                    </button>
                    <button
                      type="button"
                      onClick={() => movePhotosessionStyle(index, "down")}
                      disabled={index === getPhotosessionStyles().length - 1}
                    >
                      Вниз
                    </button>
                    <button type="button" onClick={() => removePhotosessionStyle(index)}>
                      Удалить
                    </button>
                  </div>
                </div>
              ))}
              {getPhotosessionStyles().length === 0 && (
                <div className="roulette-empty">Стили отсутствуют.</div>
              )}
            </div>
          </>
        )}
      </section>

      {/* GPT templates */}
      <section id="templates" className="roulette-section templates-section panel">
        <div className="section-header">
          <h2>Нейросеть и системные промты</h2>
          <div className="section-header-actions">
            <button
              type="button"
              className="roulette-btn"
              onClick={() => fetchTemplatesConfig(adminToken)}
              disabled={templatesLoading}
            >
              {templatesLoading ? "Обновление..." : "Обновить"}
            </button>
            <button
              type="button"
              className="roulette-btn primary"
              onClick={handleSaveTemplates}
              disabled={!templatesDirty || templatesSaving}
            >
              {templatesSaving ? "Сохранение..." : "Сохранить"}
            </button>
            <button
              type="button"
              className="section-toggle"
              onClick={() => toggleSection("templates")}
            >
              {sectionsOpen.templates ? "Свернуть" : "Развернуть"}
            </button>
          </div>
        </div>

        {sectionsOpen.templates && (
          <>
            <div className="roulette-row">
              <label className="roulette-field">
                <span>Разделов нейросети на страницу</span>
                <input
                  className="roulette-input"
                  type="number"
                  min="1"
                  max="5"
                  value={Number(templatesConfig?.neuroPageSize || 5)}
                  onChange={(e) => updateTemplatesConfig({ neuroPageSize: Number(e.target.value || 5) })}
                />
              </label>
            </div>

            <div className="roulette-item" style={{ marginBottom: 16 }}>
              <div className="roulette-row">
                <label className="roulette-field full">
                  <span>Сообщение после текстового результата: блок 1</span>
                  <textarea
                    className="roulette-textarea"
                    rows={3}
                    value={getTextResultUi().primaryText || ""}
                    onChange={(e) => updateTextResultUi({ primaryText: e.target.value })}
                  />
                </label>
              </div>

              <div className="roulette-actions" style={{ marginBottom: 12 }}>
                <button
                  type="button"
                  className="roulette-btn"
                  onClick={() => addTextResultButton("primaryButtons")}
                >
                  Добавить кнопку в блок 1
                </button>
              </div>

              <div className="roulette-list">
                {getTextResultButtons("primaryButtons").map((item, index) => (
                  <div className="roulette-item" key={`text-result-primary-${index}`}>
                    <div className="roulette-row">
                      <label className="roulette-field">
                        <span>ID</span>
                        <input
                          className="roulette-input"
                          value={item.id || ""}
                          onChange={(e) => updateTextResultButton("primaryButtons", index, { id: e.target.value })}
                        />
                      </label>
                      <label className="roulette-field">
                        <span>Текст кнопки</span>
                        <input
                          className="roulette-input"
                          value={item.label || ""}
                          onChange={(e) => updateTextResultButton("primaryButtons", index, { label: e.target.value })}
                        />
                      </label>
                      <label className="roulette-field">
                        <span>Тип</span>
                        <select
                          className="roulette-input"
                          value={item.type || "action"}
                          onChange={(e) => updateTextResultButton("primaryButtons", index, { type: e.target.value })}
                        >
                          <option value="action">action</option>
                          <option value="section">section</option>
                          <option value="menu">menu</option>
                        </select>
                      </label>
                      <label className="roulette-field">
                        <span>Видима</span>
                        <select
                          className="roulette-input"
                          value={item.active === false ? "0" : "1"}
                          onChange={(e) => updateTextResultButton("primaryButtons", index, { active: e.target.value === "1" })}
                        >
                          <option value="1">Да</option>
                          <option value="0">Нет</option>
                        </select>
                      </label>
                    </div>
                    <div className="roulette-row">
                      <label className="roulette-field full">
                        <span>Значение</span>
                        <input
                          className="roulette-input"
                          value={item.value || ""}
                          onChange={(e) => updateTextResultButton("primaryButtons", index, { value: e.target.value })}
                        />
                      </label>
                    </div>
                    <div className="mailing-button-controls">
                      <button
                        type="button"
                        onClick={() => moveTextResultButton("primaryButtons", index, "up")}
                        disabled={index === 0}
                      >
                        Вверх
                      </button>
                      <button
                        type="button"
                        onClick={() => moveTextResultButton("primaryButtons", index, "down")}
                        disabled={index === getTextResultButtons("primaryButtons").length - 1}
                      >
                        Вниз
                      </button>
                      <button type="button" onClick={() => removeTextResultButton("primaryButtons", index)}>
                        Удалить
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="roulette-row" style={{ marginTop: 16 }}>
                <label className="roulette-field full">
                  <span>Сообщение после текстового результата: блок 2</span>
                  <textarea
                    className="roulette-textarea"
                    rows={3}
                    value={getTextResultUi().secondaryText || ""}
                    onChange={(e) => updateTextResultUi({ secondaryText: e.target.value })}
                  />
                </label>
              </div>

              <div className="roulette-actions" style={{ marginBottom: 12 }}>
                <button
                  type="button"
                  className="roulette-btn"
                  onClick={() => addTextResultButton("secondaryButtons")}
                >
                  Добавить кнопку в блок 2
                </button>
              </div>

              <div className="roulette-list">
                {getTextResultButtons("secondaryButtons").map((item, index) => (
                  <div className="roulette-item" key={`text-result-secondary-${index}`}>
                    <div className="roulette-row">
                      <label className="roulette-field">
                        <span>ID</span>
                        <input
                          className="roulette-input"
                          value={item.id || ""}
                          onChange={(e) => updateTextResultButton("secondaryButtons", index, { id: e.target.value })}
                        />
                      </label>
                      <label className="roulette-field">
                        <span>Текст кнопки</span>
                        <input
                          className="roulette-input"
                          value={item.label || ""}
                          onChange={(e) => updateTextResultButton("secondaryButtons", index, { label: e.target.value })}
                        />
                      </label>
                      <label className="roulette-field">
                        <span>Тип</span>
                        <select
                          className="roulette-input"
                          value={item.type || "action"}
                          onChange={(e) => updateTextResultButton("secondaryButtons", index, { type: e.target.value })}
                        >
                          <option value="action">action</option>
                          <option value="section">section</option>
                          <option value="menu">menu</option>
                        </select>
                      </label>
                      <label className="roulette-field">
                        <span>Видима</span>
                        <select
                          className="roulette-input"
                          value={item.active === false ? "0" : "1"}
                          onChange={(e) => updateTextResultButton("secondaryButtons", index, { active: e.target.value === "1" })}
                        >
                          <option value="1">Да</option>
                          <option value="0">Нет</option>
                        </select>
                      </label>
                    </div>
                    <div className="roulette-row">
                      <label className="roulette-field full">
                        <span>Значение</span>
                        <input
                          className="roulette-input"
                          value={item.value || ""}
                          onChange={(e) => updateTextResultButton("secondaryButtons", index, { value: e.target.value })}
                        />
                      </label>
                    </div>
                    <div className="mailing-button-controls">
                      <button
                        type="button"
                        onClick={() => moveTextResultButton("secondaryButtons", index, "up")}
                        disabled={index === 0}
                      >
                        Вверх
                      </button>
                      <button
                        type="button"
                        onClick={() => moveTextResultButton("secondaryButtons", index, "down")}
                        disabled={index === getTextResultButtons("secondaryButtons").length - 1}
                      >
                        Вниз
                      </button>
                      <button type="button" onClick={() => removeTextResultButton("secondaryButtons", index)}>
                        Удалить
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <p className="mailing-help">
                Для типа <code>section</code> укажите в значении ID раздела нейросети. Для <code>action</code> можно задать любое служебное имя: сейчас такие кнопки показывают snackbar и готовы для будущей логики без правок backend.
              </p>
            </div>

            <div className="roulette-row" style={{ marginTop: 24 }}>
              <div className="roulette-field full">
                <span>Системные промты</span>
                <div className="mailing-help" style={{ marginTop: 6 }}>
                  Эти промты используются глобально в логике бота, а не внутри отдельных разделов нейросети.
                </div>
              </div>
            </div>

            <div className="roulette-list">
              {SYSTEM_PROMPT_FIELDS.map((field) => (
                <div className="roulette-item" key={`system-prompt-${field.key}`}>
                  <div className="roulette-row">
                    <label className="roulette-field full">
                      <span>{field.title}</span>
                      <textarea
                        className="roulette-textarea"
                        rows={field.key === "default" ? 6 : 10}
                        value={getSystemPrompts()[field.key] || ""}
                        onChange={(e) => updateSystemPromptValue(field.key, e.target.value)}
                      />
                    </label>
                  </div>
                  <div className="mailing-help">{field.description}</div>
                </div>
              ))}
            </div>

            <div className="roulette-actions" style={{ marginBottom: 12 }}>
              <button type="button" className="roulette-btn" onClick={addNeuroSection}>
                Добавить раздел
              </button>
            </div>

            <p className="mailing-help">
              Здесь настраиваются кнопки разделов нейросети в боте: название, стоимость, видимость,
              порядок, тип генерации, экран входа и шаблон результата.
            </p>

            <div className="roulette-list">
              {getNeuroSections().map((item, index) => (
                <div className="roulette-item" key={`neuro-section-${index}`}>
                  <div className="roulette-row">
                    <label className="roulette-field">
                      <span>ID</span>
                      <input
                        className="roulette-input"
                        value={item.id || ""}
                        onChange={(e) => updateNeuroSection(index, { id: e.target.value })}
                      />
                    </label>
                    <label className="roulette-field">
                      <span>Кнопка</span>
                      <input
                        className="roulette-input"
                        value={item.title || ""}
                        onChange={(e) => updateNeuroSection(index, { title: e.target.value })}
                      />
                    </label>
                    <label className="roulette-field">
                      <span>Видим</span>
                      <select
                        className="roulette-input"
                        value={item.active === false ? "0" : "1"}
                        onChange={(e) => updateNeuroSection(index, { active: e.target.value === "1" })}
                      >
                        <option value="1">Да</option>
                        <option value="0">Нет</option>
                      </select>
                    </label>
                    <label className="roulette-field">
                      <span>Тип</span>
                      <select
                        className="roulette-input"
                        value={item.type || "text"}
                        onChange={(e) => updateNeuroSection(index, { type: e.target.value })}
                      >
                        <option value="text">text / ChatGPT</option>
                        <option value="image">image</option>
                        <option value="video">video</option>
                        <option value="audio">audio</option>
                      </select>
                    </label>
                    <label className="roulette-field">
                      <span>Режим</span>
                      <select
                        className="roulette-input"
                        value={item.sessionMode || "single"}
                        onChange={(e) => updateNeuroSection(index, { sessionMode: e.target.value })}
                      >
                        <option value="single">Одно сообщение</option>
                        <option value="chat">Диалог</option>
                      </select>
                    </label>
                  </div>

                  <div className="roulette-row">
                    <label className="roulette-field">
                      <span>Стоимость, б.</span>
                      <input
                        className="roulette-input"
                        type="number"
                        min="0"
                        value={Number(item.cost || 0)}
                        onChange={(e) => updateNeuroSection(index, { cost: Number(e.target.value || 0) })}
                      />
                    </label>
                    <label className="roulette-field">
                      <span>Показывать цену на кнопке</span>
                      <select
                        className="roulette-input"
                        value={item.showCostOnButton === true ? "1" : "0"}
                        onChange={(e) => updateNeuroSection(index, { showCostOnButton: e.target.value === "1" })}
                      >
                        <option value="0">Нет</option>
                        <option value="1">Да</option>
                      </select>
                    </label>
                    <label className="roulette-field">
                      <span>Медиа при входе (attachment)</span>
                      <input
                        className="roulette-input"
                        value={item.entryAttachment || ""}
                        onChange={(e) => updateNeuroSection(index, { entryAttachment: e.target.value })}
                      />
                    </label>
                    <label className="roulette-field">
                      <span>Медиа в результате (attachment)</span>
                      <input
                        className="roulette-input"
                        value={item.resultAttachment || ""}
                        onChange={(e) => updateNeuroSection(index, { resultAttachment: e.target.value })}
                      />
                    </label>
                  </div>

                  <div className="roulette-row">
                    <label className="roulette-field full">
                      <span>Текст экрана входа</span>
                      <textarea
                        className="roulette-textarea"
                        rows={4}
                        value={item.entryText || ""}
                        onChange={(e) => updateNeuroSection(index, { entryText: e.target.value })}
                      />
                    </label>
                  </div>

                  <div className="roulette-row">
                    <label className="roulette-field full">
                      <span>System prompt / логика генерации</span>
                      <textarea
                        className="roulette-textarea"
                        rows={6}
                        value={item.prompt || ""}
                        onChange={(e) => updateNeuroSection(index, { prompt: e.target.value })}
                      />
                    </label>
                  </div>

                  <div className="roulette-row">
                    <label className="roulette-field full">
                      <span>Шаблон ответа пользователю</span>
                      <textarea
                        className="roulette-textarea"
                        rows={5}
                        value={item.resultText || ""}
                        onChange={(e) => updateNeuroSection(index, { resultText: e.target.value })}
                      />
                    </label>
                  </div>

                  <p className="mailing-help">
                    Можно использовать переменные: <code>{"{{result}}"}</code>, <code>{"{{prompt}}"}</code>, <code>{"{{title}}"}</code>.
                    Стоимость `0` означает бесплатный раздел.
                  </p>

                  <div className="mailing-button-controls">
                    <button
                      type="button"
                      onClick={() => moveNeuroSection(index, "up")}
                      disabled={index === 0}
                    >
                      Вверх
                    </button>
                    <button
                      type="button"
                      onClick={() => moveNeuroSection(index, "down")}
                      disabled={index === getNeuroSections().length - 1}
                    >
                      Вниз
                    </button>
                    <button type="button" onClick={() => removeNeuroSection(index)}>
                      Удалить
                    </button>
                  </div>
                </div>
              ))}
              {getNeuroSections().length === 0 && (
                <div className="roulette-empty">Разделы нейросети отсутствуют.</div>
              )}
            </div>

            <div className="roulette-row" style={{ marginTop: 24 }}>
              <label className="roulette-field">
                <span>Legacy GPT-шаблонов на страницу</span>
                <input
                  className="roulette-input"
                  type="number"
                  min="1"
                  max="10"
                  value={Number(templatesConfig?.pageSize || 5)}
                  onChange={(e) => updateTemplatesConfig({ pageSize: Number(e.target.value || 5) })}
                />
              </label>
            </div>

            <div className="roulette-actions" style={{ marginBottom: 12 }}>
              <button type="button" className="roulette-btn" onClick={addTemplatePrompt}>
                Добавить legacy шаблон
              </button>
            </div>

            <div className="roulette-list">
              {getTemplatePrompts().map((item, index) => (
                <div className="roulette-item" key={`template-prompt-${index}`}>
                  <div className="roulette-row">
                    <label className="roulette-field">
                      <span>ID</span>
                      <input
                        className="roulette-input"
                        value={item.id || ""}
                        onChange={(e) => updateTemplatePrompt(index, { id: e.target.value })}
                      />
                    </label>
                    <label className="roulette-field">
                      <span>Эмодзи</span>
                      <select
                        className="roulette-input"
                        value={item.emoji || ""}
                        onChange={(e) => updateTemplatePrompt(index, { emoji: e.target.value })}
                      >
                        <option value="">Выбрать...</option>
                        {item.emoji && !TEMPLATE_EMOJI_PRESETS.includes(item.emoji) ? (
                          <option value={item.emoji}>{item.emoji}</option>
                        ) : null}
                        {TEMPLATE_EMOJI_PRESETS.map((emoji) => (
                          <option key={`template-emoji-option-${index}-${emoji}`} value={emoji}>
                            {emoji}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="roulette-field">
                      <span>Название</span>
                      <input
                        className="roulette-input"
                        value={item.title || ""}
                        onChange={(e) => updateTemplatePrompt(index, { title: e.target.value })}
                      />
                    </label>
                    <label className="roulette-field">
                      <span>Активен</span>
                      <select
                        className="roulette-input"
                        value={item.active === false ? "0" : "1"}
                        onChange={(e) => updateTemplatePrompt(index, { active: e.target.value === "1" })}
                      >
                        <option value="1">Да</option>
                        <option value="0">Нет</option>
                      </select>
                    </label>
                  </div>

                  <div className="roulette-row">
                    <label className="roulette-field full">
                      <span>System prompt</span>
                      <textarea
                        className="roulette-textarea"
                        rows={6}
                        value={item.prompt || ""}
                        onChange={(e) => updateTemplatePrompt(index, { prompt: e.target.value })}
                      />
                    </label>
                  </div>

                  <div className="mailing-button-controls">
                    <button
                      type="button"
                      onClick={() => moveTemplatePrompt(index, "up")}
                      disabled={index === 0}
                    >
                      Вверх
                    </button>
                    <button
                      type="button"
                      onClick={() => moveTemplatePrompt(index, "down")}
                      disabled={index === getTemplatePrompts().length - 1}
                    >
                      Вниз
                    </button>
                    <button type="button" onClick={() => removeTemplatePrompt(index)}>
                      Удалить
                    </button>
                  </div>
                </div>
              ))}
              {getTemplatePrompts().length === 0 && (
                <div className="roulette-empty">Legacy шаблоны отсутствуют.</div>
              )}
            </div>
          </>
        )}
      </section>

      {/* Статистика */}
      <section id="stats" className="stats-section panel">
        <div className="section-header">
          <h2>Статистика</h2>
          <button
            type="button"
            className="section-toggle"
            onClick={() => toggleSection("stats")}
          >
            {sectionsOpen.stats ? "Свернуть" : "Развернуть"}
          </button>
        </div>
        {sectionsOpen.stats && (
          <>
            <div className="stats-cards">
              <div className="card">
                <div className="metric-title">Регистрации за 7 дней</div>
                <div className="metric-value">{stats.registrations.join(", ")}</div>
                <div className="metric-hint">Новые пользователи по дням за последние 7 суток.</div>
              </div>
              <div className="card">
                <div className="metric-title">Активных за сутки</div>
                <div className="metric-value">{stats.dailyActive}</div>
                <div className="metric-hint">Уникальные пользователи с активностью за последние 24 часа.</div>
              </div>
              <div className="card">
                <div className="metric-title">Всего пользователей</div>
                <div className="metric-value">{stats.totalUsers}</div>
                <div className="metric-hint">Общее количество пользователей в базе.</div>
              </div>
              <div className="card">
                <div className="metric-title">Premium пользователи</div>
                <div className="metric-value">
                  Lite {stats.lite.count}, Pro {stats.pro.count}, Max {stats.premium.count}
                </div>
                <div className="metric-hint">Пользователи с активным premium_id по планам.</div>
              </div>
              <div className="card total">
                <div className="metric-title">Общий доход</div>
                <div className="metric-value">{stats.lite.sum + stats.pro.sum + stats.premium.sum} руб</div>
                <div className="metric-hint">Оценка: активные подписки × фиксированная цена ({planPricesText}).</div>
              </div>
            </div>

            <div className="chart-container">
              <Line
                data={{
                  labels: labels,
                  datasets: [
                    {
                      label: "Регистрации",
                      data: stats.registrations,
                      borderColor: "rgba(75,192,192,1)",
                      backgroundColor: "rgba(75,192,192,0.2)"
                    }
                  ]
                }}
                options={{ responsive: true }}
              />
            </div>
          </>
        )}
      </section>

      {/* Управление пользователем */}
      <section id="users" className="users-section panel">
        <div className="section-header">
          <h2>Управление пользователем</h2>
          <button
            type="button"
            className="section-toggle"
            onClick={() => toggleSection("user")}
          >
            {sectionsOpen.user ? "Свернуть" : "Развернуть"}
          </button>
        </div>
        {sectionsOpen.user && (
          <>
            <div className="search-user">
              <input
                type="text"
                value={searchId}
                placeholder="ID пользователя"
                onChange={(e) => setSearchId(e.target.value)}
              />
              <button onClick={handleSearch}>Поиск</button>
            </div>

            {user && (
              <div className="user-card">
                <pre>
{`ID: ${user.id}
UID: ${user.uid}
Имя: ${user.first_name}
Пригласил: ${user.ref_activation || "-"}
Кол-во приглашений: ${user.ref_count || 0}
Баланс: ${user.balance || 0}
Подписка: ${user.premium_id || 0}
Подписка до: ${user.premium_time ? new Date(user.premium_time * 1000).toLocaleString() : "-"}
Платежный индикатор: ${user.settings_payment_id || "-"}
След. платеж: ${user.settings_payment_time ? new Date(user.settings_payment_time * 1000).toLocaleString() : "-"}`}
                </pre>

                <div className="action-form">
                  <input
                    type="number"
                    placeholder="Сумма баланса (+/-)"
                    value={actionData.balance}
                    onChange={(e) => setActionData({ ...actionData, balance: e.target.value })}
                  />
                  <button onClick={() => handleAction("balance")}>Выдать/забрать баланс</button>

                  <select
                    value={actionData.premiumId}
                    onChange={(e) => setActionData({ ...actionData, premiumId: e.target.value })}
                  >
                    <option value={1}>Lite</option>
                    <option value={2}>Pro</option>
                    <option value={3}>Max</option>
                    <option value={0}>Снять подписку</option>
                  </select>
                  <input
                    type="number"
                    placeholder="Дней подписки"
                    value={actionData.premiumDays}
                    onChange={(e) => setActionData({ ...actionData, premiumDays: e.target.value })}
                  />
                  <button onClick={() => handleAction("premium")}>Выдать/Снять подписку</button>
                </div>
              </div>
            )}
          </>
        )}
      </section>

      {/* Рассылка */}
      <section id="mailing" className="mailing-section panel">
        <div className="section-header">
          <h2>Рассылка</h2>
          <button
            type="button"
            className="section-toggle"
            onClick={() => toggleSection("mailing")}
          >
            {sectionsOpen.mailing ? "Свернуть" : "Развернуть"}
          </button>
        </div>
        {sectionsOpen.mailing && (
          <>
            <div className="mailing-form">
          <textarea
            rows={6}
            placeholder="Текст рассылки"
            value={mailingData.text}
            onChange={(e) => setMailingData({ ...mailingData, text: e.target.value })}
          />

          <div className="mailing-row">
            <select
              value={mailingData.target}
              onChange={(e) => setMailingData({ ...mailingData, target: e.target.value })}
            >
              <option value="all">Всем</option>
              <option value="conversations">Беседы</option>
              <option value="donates">Только донатам</option>
              <option value="new">Новым</option>
              <option value="test">Тест (только админу)</option>
            </select>
            <input
              type="number"
              min="0"
              placeholder="Старт после ID"
              value={mailingStartFromUserId}
              onChange={(e) => setMailingStartFromUserId(e.target.value)}
            />
          </div>
          <div className="mailing-row">
            <label className="mailing-rate">
              Скорость: {Math.max(1, Number(mailingRate || 1))} /сек
            </label>
            <input
              type="range"
              min="1"
              max="100"
              step="1"
              value={mailingRate}
              onChange={(e) => setMailingRate(Number(e.target.value))}
            />
            <div className="mailing-rate-hint">
              Примерное время: {formatEta(getEstimatedEtaSec())} (оценка по выбранной аудитории)
              {mailingPreview && Number.isFinite(mailingPreview.startOffset) ? (
                <> · После старта останется: {mailingPreview.remaining}</>
              ) : null}
            </div>
          </div>

          <div className="mailing-row mailing-buttons-actions">
            <button type="button" onClick={() => addMailingButton("url")}>
              Добавить URL кнопку
            </button>
            <button type="button" onClick={() => addMailingButton("callback")}>
              Добавить Callback кнопку
            </button>
          </div>

          {mailingButtons.length > 0 ? (
            <div className="mailing-buttons">
              {mailingButtons.map((btn, index) => (
                <div className="mailing-button-item" key={btn.id}>
                  <div className="mailing-button-header">
                    <span>Кнопка #{index + 1}</span>
                    <div className="mailing-button-controls">
                      <button
                        type="button"
                        onClick={() => moveMailingButton(btn.id, "up")}
                        disabled={index === 0}
                      >
                        Вверх
                      </button>
                      <button
                        type="button"
                        onClick={() => moveMailingButton(btn.id, "down")}
                        disabled={index === mailingButtons.length - 1}
                      >
                        Вниз
                      </button>
                      <button type="button" onClick={() => removeMailingButton(btn.id)}>
                        Удалить
                      </button>
                    </div>
                  </div>

                  <div className="mailing-row">
                    <select
                      value={btn.type}
                      onChange={(e) => updateMailingButton(btn.id, { type: e.target.value })}
                    >
                      <option value="url">URL</option>
                      <option value="callback">Callback</option>
                    </select>
                    <input
                      type="text"
                      placeholder="Текст кнопки"
                      value={btn.text}
                      onChange={(e) => updateMailingButton(btn.id, { text: e.target.value })}
                    />
                  </div>

                  <div className="mailing-row">
                    <input
                      type="text"
                      placeholder={btn.type === "url" ? "https://..." : "payload"}
                      value={btn.value}
                      onChange={(e) => updateMailingButton(btn.id, { value: e.target.value })}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="mailing-help">Кнопки не добавлены.</p>
          )}

          <div className="mailing-row">
            <input
              type="text"
              placeholder="Вложения VK: wall-123_456, video-123_456, photo-123_456"
              value={mailingData.attachments}
              onChange={(e) => setMailingData({ ...mailingData, attachments: e.target.value })}
            />
          </div>

          <div className="mailing-row">
            <input
              key={mailingFileKey}
              type="file"
              accept="image/*"
              multiple
              onChange={handleMailingImagesChange}
            />
            <div className="mailing-images">
              {mailingImages.length > 0
                ? mailingImages.map((file) => (
                    <span key={`${file.name}-${file.lastModified}`}>{file.name}</span>
                  ))
                : "Изображения не выбраны"}
            </div>
          </div>

          <p className="mailing-help">
            Вложения: укажите VK-идентификаторы через запятую (wall-..., video-..., photo-...).
          </p>
          <p className="mailing-help">
            Новые пользователи — зарегистрированы за последние 7 дней. Для бесед отправка идёт по peer_id 2000000001–2000002000. Максимум 10 изображений. URL должен начинаться с http/https.
          </p>

          <div className="mailing-actions">
            <button onClick={handleSendMailing} disabled={mailingDisabled}>
              {mailingSending ? "Отправка..." : "Запустить рассылку"}
            </button>
            <button onClick={resetMailing} disabled={mailingDisabled}>
              Очистить
            </button>
          </div>
        </div>

        {mailingProgress && (
          <div className={`mailing-progress ${mailingProgress.status}`}>
            <div className="mailing-progress-header">
              {mailingProgress.status === "running"
                ? "Рассылка выполняется"
                : mailingProgress.status === "done"
                ? "Рассылка завершена"
                : "Рассылка завершилась с ошибкой"}
            </div>
            <div className="mailing-progress-bar">
              <div
                className="mailing-progress-fill"
                style={{ width: `${Math.min(100, mailingProgress.percent || 0)}%` }}
              />
            </div>
          <div className="mailing-progress-text">
            Отправлено: {mailingProgress.sent} / {mailingProgress.total} (ошибок: {mailingProgress.failed}) — {mailingProgress.percent || 0}%
          </div>
          <div className="mailing-progress-text">
            Скорость: {mailingProgress.ratePerSec || "—"} /сек — Осталось примерно: {formatEta(mailingProgress.etaSec)}
          </div>
        </div>
      )}


        {mailingResult && (
          <div className={`mailing-result ${mailingResult.success ? "success" : "error"}`}>
            {mailingResult.message}
          </div>
        )}
          </>
        )}
      </section>
      {/* Поиск лога по ID */}
      <section id="logs-by-id" className="logs-section panel">
        <div className="section-header">
          <h2>Поиск лога по ID</h2>
          <button
            type="button"
            className="section-toggle"
            onClick={() => toggleSection("logById")}
          >
            {sectionsOpen.logById ? "Свернуть" : "Развернуть"}
          </button>
        </div>
        {sectionsOpen.logById && (
          <>
            <div className="logs-controls">
              <input
                type="text"
                value={logId}
                placeholder="ID лога"
                onChange={(e) => setLogId(e.target.value)}
              />
              <button onClick={handleSearchLogById}>Поиск</button>
            </div>

            {log && (
              <div className="log-card">
                <pre>
      {`ID: ${log.id}
      User ID: ${log.user_id}
      Вопрос: ${log.user_question}
      Ответ GPT: ${log.gpt_answer}
      Дата: ${new Date(log.created_at).toLocaleString()}`}
                </pre>
              </div>
            )}
          </>
        )}
      </section>

      {/* Логи пользователя по user_id */}
      <section id="logs-by-user" className="logs-section panel">
        <div className="section-header">
          <h2>Логи пользователя</h2>
          <button
            type="button"
            className="section-toggle"
            onClick={() => toggleSection("logsUser")}
          >
            {sectionsOpen.logsUser ? "Свернуть" : "Развернуть"}
          </button>
        </div>
        {sectionsOpen.logsUser && (
          <>
            <div className="logs-controls">
              <input
                type="text"
                value={searchId}
                placeholder="user_id"
                onChange={(e) => setSearchId(e.target.value)}
              />
              <button onClick={handleSearchLogsByUser}>Показать логи</button>
            </div>

            {userLogs.length > 0 && (
              <div className="logs-table-container">
                <table className="logs-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>User ID</th>
                      <th>Вопрос</th>
                      <th>Ответ GPT</th>
                      <th>Дата</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userLogs.map((l) => (
                      <tr key={l.id}>
                        <td>{l.id}</td>
                        <td>{l.user_id}</td>
                        <td>{l.user_question}</td>
                        <td>{l.gpt_answer}</td>
                        <td>{new Date(l.created_at).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </section>

      {/* График сообщений */}
      <section id="logs-chart" className="logs-chart-container panel">
        <div className="section-header">
          <h2>Сообщения за последние 7 дней</h2>
          <button
            type="button"
            className="section-toggle"
            onClick={() => toggleSection("logsChart")}
          >
            {sectionsOpen.logsChart ? "Свернуть" : "Развернуть"}
          </button>
        </div>
        {sectionsOpen.logsChart && (
          <div className="chart-container">
            <Line
              data={{
                labels: logsLabels,
                datasets: [
                  {
                    label: "Количество сообщений",
                    data: logsData,
                    borderColor: "rgba(75,192,192,1)",
                    backgroundColor: "rgba(75,192,192,0.2)",
                  },
                ],
              }}
              options={{ responsive: true }}
            />
          </div>
        )}
      </section>
      {/* Последние 100 логов */}
      <section id="logs-last" className="logs-section panel">
        <div className="section-header">
          <h2>Последние 100 логов</h2>
          <button
            type="button"
            className="section-toggle"
            onClick={() => toggleSection("lastLogs")}
          >
            {sectionsOpen.lastLogs ? "Свернуть" : "Развернуть"}
          </button>
        </div>

        {sectionsOpen.lastLogs && (
          <>
            {lastLogs.length > 0 ? (
              <div className="logs-table-container">
                <table className="logs-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>User ID</th>
                      <th>Вопрос</th>
                      <th>Ответ GPT</th>
                      <th>Дата</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lastLogs.map((l) => (
                      <tr key={l.id}>
                        <td>{l.id}</td>
                        <td>{l.user_id}</td>
                        <td>{l.user_question}</td>
                        <td>{l.gpt_answer}</td>
                        <td>{new Date(l.created_at).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p>Логи отсутствуют</p>
            )}
          </>
        )}
      </section>
        </main>
      </div>
    </div>
  );
}


